<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true" :stickyHeader="!isEmbedded")
  template(#header-leading v-if="!isEmbedded")
    button.header-back-btn(@click="goBack" type="button" aria-label="Go back")
      img(src="@/assets/icons/chevron-right.svg" alt="").back-icon
  SmartBanner
  .webStickerBody(:class="{ embedded: isEmbedded }")
    .embed-back-header(v-if="isEmbedded && !isStandaloneEmbed")
      button.embed-back-btn(@click="handleEmbedBack" type="button" aria-label="Back to organization")
        img(src="@/assets/icons/chevron-right.svg" alt="").embed-back-icon
        span Back

    //- Error Message
    div(v-if="error")
      .error-text {{ error }}

    //- Published Sticker
    .sticker-content-wrapper(v-else-if="item.published === true")
      //- Event Banner (Ticket only)
      .event-banner(v-if="item.campaignType === 'Ticket' && item.bannerUrl")
        img(:src="item.bannerUrl" alt="Event Banner").banner-img

        //- Date/Location Overlay (Bottom)
        .banner-overlay-info
          //- Floating Sticker Badge (positioned above overlay)
          .floating-sticker-badge(v-if="item.url" @click="handleCollectBadgeClick")
            img(:src="item.url" alt="Collectible Sticker").sticker-thumbnail
            .badge-text Collect Sticker with RSVP

          .overlay-content
            .date-time-info(v-if="item.eventDate")
              span.date-text {{ getformattedDate(item.eventDate) }}
              span.separator •
              span.time-text {{ getformattedTime(item.eventDate) }}
            .location-info(v-if="item.location")
              span.location-text {{ item.location }}

      //- View count + insights link (ticket with banner)
      .sticker-analytics-bar(
        v-if="showInsightsBar && item.campaignType === 'Ticket' && item.bannerUrl"
      )
        img(v-if="item.viewCount != null" src="@/assets/icons/eye.svg" alt="Views").analytics-eye-icon
        span.analytics-count(v-if="item.viewCount != null") {{ formatCompactCount(item.viewCount) }}
        span.analytics-separator(v-if="hostCheckReady && isEventHost && item.viewCount != null") •
        button.analytics-link(
          v-if="hostCheckReady && isEventHost"
          type="button"
          @click="navigateToStickerAnalytics"
        ) View insights

      .webSticker(:class="{ 'ticket-banner-layout': item.campaignType === 'Ticket' && item.bannerUrl }")
        //- Left Column - Sticker Display (Hidden for Tickets with Banner)
        .sticker-column-left(v-if="!(item.campaignType === 'Ticket' && item.bannerUrl)")
          .sticker-display-wrapper
            .sticker-image-container
              img(
                v-if="item.url"
                :src="item.url"
                alt="Sticker Image"
                @error="handleStickerImageError"
              ).sticker-image

            //- Collect Sticker Badge
            .collect-badge(@click="handleCollectBadgeClick")
              img(src="@/assets/icons/plusWhite.svg" alt="").collect-badge-icon
              span Collect Sticker

            //- Glow Badge (conditional)
            .glow-badge(v-if="item.glow" @click="handleGlowClick")
              svg.glow-badge-icon(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor")
                path(d="M13 2L3 14h8l-1 8 10-12h-8l1-8z")
              .glow-badge-content
                .glow-badge-title Glow Event
                .glow-badge-date(v-if="item.glow.glowStartDate") {{ formatGlowDate(item.glow.glowStartDate) }} • {{ formatGlowTime(item.glow.glowStartDate) }}

          //- Tabs Navigation (Desktop Only)
          .tabs-navigation.desktop-only
            button(
              v-for="tab in tabs"
              :key="tab"
              @click="activeTab = tab"
              :class="{ active: activeTab === tab }"
              type="button"
            ).tab-button {{ tab }}

          //- Content Sections
          .content-sections
            //- Mobile: Show all sections stacked
            //- Desktop: Show active tab only

            //- Amount Input (Mobile only — right above Details)
            .amount-input-section.amount-input-section--mobile(v-if="item.quickCosts && item.quickCosts.some((cost) => cost !== null)")
              label.amount-label Enter an amount (USD)
              .amount-input-wrapper(ref="amountInputWrapperRef" :class="{ 'amount-input-highlight': amountInputHighlight }")
                span.dollar-sign $
                input(
                  v-model="customAmount"
                  @input="validateCustomAmount"
                  type="number"
                  min="0.01"
                  placeholder="0"
                  aria-label="Enter custom amount in USD"
                ).amount-input
              .preset-amounts
                button(
                  v-for="quickCost in item.quickCosts"
                  :key="quickCost"
                  @click="customAmount = quickCost"
                  :class="{ active: customAmount == quickCost }"
                  type="button"
                ).preset-button ${{ quickCost }}

            //- Details Section
            .content-section.details-section(
              :class="{ 'desktop-hidden': activeTab !== 'Details' }"
            )
              h3.section-heading.mobile-only Details
              .section-description(v-html="item.description")

            //- About Creator Section
            .content-section.creator-section(
              :class="{ 'desktop-hidden': activeTab !== 'About Creator' }"
            )
              h3.section-heading.mobile-only About Creator
              .creator-info-details
                .creator-details-section
                  .creator-detail-item(v-if="item.organization?.name || (item.user?.firstname && item.user?.lastname)")
                    .detail-label Name
                    .detail-value {{ item.organization?.name || `${item.user?.firstname} ${item.user?.lastname}` }}

                  .creator-detail-item(v-if="item.organization?.email")
                    .detail-label Email
                    .detail-value
                      a(:href="`mailto:${item.organization.email}`") {{ item.organization.email }}

                  .creator-detail-item(v-if="item.organization?.phone")
                    .detail-label Phone
                    .detail-value
                      a(:href="`tel:${item.organization.phone}`") {{ item.organization.phone }}

            //- Updates Section
            .content-section.updates-section(
              :class="{ 'desktop-hidden': activeTab !== 'Updates' }"
            )
              h3.section-heading.mobile-only Recent Updates
              .section-description
                p Stay tuned for updates on how your contributions are making a difference!

            //- Announcements Section
            .content-section.announcements-section(
              :class="{ 'desktop-hidden': activeTab !== 'Announcements' }"
            )
              h3.section-heading.mobile-only Announcements
              .announcements-loading(v-if="announcementsLoading")
                p Loading announcements...
              .announcements-list(v-else-if="stickerAnnouncements.length")
                .announcement-item(v-for="announcement in stickerAnnouncements" :key="announcement.id")
                  .announcement-subject {{ announcement.subject }}
                  .announcement-date {{ formatAnnouncementDate(announcement.sentAt) }}
                  .announcement-body {{ announcement.body }}
              .announcements-empty(v-else)
                p No announcements yet

        //- Right Column - Details & Form
        .sticker-column-right
          //- Campaign Type Badge
          .campaign-badge(
            v-if="item.campaignType"
            :class="{ 'is-clickable': isCampaignBadgeClickable }"
            role="button"
            tabindex="0"
            @click="handleCampaignBadgeClick"
            @keydown.enter.prevent="handleCampaignBadgeClick"
            @keydown.space.prevent="handleCampaignBadgeClick"
          )
            .campaign-badge-dot
            span.campaign-badge-text {{ item.campaignType.toUpperCase() }}

          //- Creator Info
          .creator-info(
            :class="{ 'is-clickable': creatorProfilePath }"
            @click="handleCreatorClick"
          )
            .creator-avatar
              img(
                v-if="item.organization?.avatar"
                :src="item.organization.avatar"
                alt="Creator Avatar"
                @error="handleAvatarImageError"
              )
              img(
                v-else-if="item.user?.avatar"
                :src="item.user.avatar"
                alt="Creator Avatar"
                @error="handleAvatarImageError"
              )
              span.creator-initial(v-else) {{ creatorInitial }}
            .creator-details
              .creator-name {{ creatorName }}
              .creator-verified Verified Creator

          //- Title
          h2.sticker-title {{ item.title }}

          //- View count + insights link (primary placement — visible on desktop + mobile)
          .sticker-analytics-bar(
            v-if="showInsightsBar && !(item.campaignType === 'Ticket' && item.bannerUrl)"
          )
            img(v-if="item.viewCount != null" src="@/assets/icons/eye.svg" alt="Views").analytics-eye-icon
            span.analytics-count(v-if="item.viewCount != null") {{ formatCompactCount(item.viewCount) }}
            span.analytics-separator(v-if="hostCheckReady && isEventHost && item.viewCount != null") •
            button.analytics-link(
              v-if="hostCheckReady && isEventHost"
              type="button"
              @click="navigateToStickerAnalytics"
            ) View insights

          //- Price Range
          .price-range(v-if="!item?.quickCosts?.length")
            .price-with-fees(v-if="item.priceRange")
              .price-amount {{ formatTicketPriceRangeFromString(item.priceRange) }}
              .price-label incl. fees
            span(v-else-if="item.cost == 0 && !item.varients.length && !item.sweepstakesSticker") FREE
            .price-with-fees(v-else-if="item.varients.length && variantsRange !== 'Free'")
              .price-amount {{ variantsRange }}
              .price-label incl. fees
            span(v-else-if="item.varients.length && variantsRange === 'Free'") FREE
            .price-with-fees(v-else-if="item.sweepstakesSticker && sweepstakesRange !== 'Free'")
              .price-amount {{ sweepstakesRange }}
              .price-label incl. fees
            span(v-else-if="item.sweepstakesSticker && sweepstakesRange === 'Free'") FREE
            .price-with-fees(v-else-if="item.cost > 0")
              .price-amount {{ formatPriceAmountWithFees(item.cost) }}
              .price-label incl. fees

          .price-range(v-if="item?.quickCosts?.length")
            .price-with-fees(v-if="item.priceRange")
              .price-amount {{ formatTicketPriceRangeFromString(item.priceRange) }}
              .price-label incl. fees
            .price-with-fees(v-else-if="quickCostsRange !== 'Free'")
              .price-amount {{ quickCostsRange }}
              .price-label incl. fees
            span(v-else) FREE

          //- Recipient Charity Info (for campaigns) - RIGHT AFTER PRICE
          .charity-info(v-if="item.campaignType === 'Campaign' && item.causes?.length === 1")
            span.charity-label Cause:
            span.charity-name {{ getCauseName(item.causes[0]) }}
            span.charity-ein  | EIN: {{ getCauseEIN(item.causes[0]) }}

          //- Amount Input (Desktop only — on mobile it renders just above Details)
          .amount-input-section.amount-input-section--desktop(v-if="item.quickCosts && item.quickCosts.some((cost) => cost !== null)")
            label.amount-label Enter an amount (USD)
            .amount-input-wrapper(
              ref="amountInputDesktopWrapperRef"
              :class="{ 'amount-input-highlight': amountInputHighlight }"
            )
              span.dollar-sign $
              input(
                v-model="customAmount"
                @input="validateCustomAmount"
                type="number"
                min="0.01"
                placeholder="0"
                id="custom-amount-input"
                aria-label="Enter custom amount in USD"
              ).amount-input

            //- Preset Amounts
            .preset-amounts
              button(
                v-for="quickCost in item.quickCosts"
                :key="quickCost"
                @click="customAmount = quickCost"
                :class="{ active: customAmount == quickCost }"
                type="button"
              ).preset-button ${{ quickCost }}

          //- Sweepstakes Start/End Dates
          .sweepstakes-dates(v-if="item.campaignType === 'Sweepstakes' && (item.startDate || item.endDate)")
            .row
              .col-6(v-if="item.startDate")
                h6.date-label Starts
                .date-value {{ formatDateTimeWithTimezone(item.startDate, item.startDateTimeZone) }}
              .col-6(v-if="item.endDate")
                h6.date-label Ends
                .date-value {{ formatDateTimeWithTimezone(item.endDate, item.endDateTimeZone) }}

          //- Sweepstakes Slider
          .sweepstakes-slider(v-if="item.campaignType === 'Sweepstakes'")
            //- Prize Info Box
            .sweepstakes-prize-box(v-if="item.sweepstakesSticker.approximateRetailValue || item.sweepstakesSticker.prizes")
              .prize-header
                .prize-label Prize:
                .prize-name {{ item.sweepstakesSticker.approximateRetailValue || 'Grand Prize' }}
              .prize-details(v-if="item.sweepstakesSticker.prizes" v-html="item.sweepstakesSticker.prizes")

            //- Draw Date Info
            .sweepstakes-dates-row(v-if="item.sweepstakesSticker.drawingDateTime || item.endDate")
              .date-col(v-if="item.endDate")
                .date-label Entries Close:
                .date-value {{ formatDateTimeWithTimezone(item.endDate, item.endDateTimeZone) }}
              .date-col(v-if="item.sweepstakesSticker.drawingDateTime")
                .date-label Draw Date:
                .date-value {{ formatDateTimeWithTimezone(item.sweepstakesSticker.drawingDateTime, item.sweepstakesSticker.drawingDateTimeTimezone) }}

            //- Slider (existing)
            .slider-header
              span.slider-label {{ currentEntryText }}
              span.slider-value {{ currentCostText }}
            VueSlider(
              v-model="value"
              :adsorb="true"
              :min="0"
              :max="item.sweepstakesSticker.entries.length"
              :marks="marks"
              tooltip="none"
              :processStyle="{ backgroundColor: '#0d9488' }"
            ).custom-slider
            .dots-container
              span(
                v-for="dot in item.sweepstakesSticker.entries.length"
                :key="dot"
                :class="{ active: dot - 1 === value }"
                @click="selectValue(dot - 1)"
                @keydown.enter="selectValue(dot - 1)"
                @keydown.space="selectValue(dot - 1)"
                tabindex="0"
                role="button"
                :aria-label="`Select ${dot} entries`"
              ).dot

            //- Legal Disclaimer
            .sweepstakes-legal
              p.legal-text
                strong NO PURCHASE NECESSARY.
                | Purchase will not increase chances of winning.
              p.legal-details
                | Must be 18+ and a legal US resident. Void where prohibited.
              button.legal-link(@click.stop="showSweepstakesRules = true" type="button") View Official Rules

          //- Event Date/Time (for tickets)
          .event-datetime(v-if="item.eventDate && item.campaignType === 'Ticket'")
            svg.event-datetime-icon(
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            )
              rect(x="3" y="4" width="18" height="18" rx="2" ry="2")
              line(x1="16" y1="2" x2="16" y2="6")
              line(x1="8" y1="2" x2="8" y2="6")
              line(x1="3" y1="10" x2="21" y2="10")
            span.event-datetime-text {{ getFormattedEventDateTime(item.eventDate) }}

          //- Event Date/Time non-ticket (keep original grid layout)
          .event-datetime-grid(v-else-if="item.eventDate")
            .row
              .col-6
                h5.event-value {{ getformattedDate(item.eventDate) }}
                .event-label Date
              .col-6
                h5.event-value {{ getformattedTime(item.eventDate) }}
                .event-label Time

          //- Location: physical address with pin icon
          .event-location(
            v-if="item.campaignType === 'Ticket' && !item.hideLocation && item.location && item.locationType !== 'virtual'"
          )
            .location-icon-row
              svg.location-icon(
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              )
                path(d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z")
              span {{ item.location }}

          //- Location: virtual event — CTA card only, no pin icon
          a.virtual-link-cta(
            v-if="item.campaignType === 'Ticket' && !item.hideLocation && item.locationType === 'virtual' && item.virtualLink"
            :href="normalizedVirtualLink"
            target="_blank"
            rel="noopener noreferrer"
          )
            svg.virtual-link-icon(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true")
              path(d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6")
              polyline(points="15 3 21 3 21 9")
              line(x1="10" y1="14" x2="21" y2="3")
            .virtual-link-content
              span.virtual-link-label Join Online
              span.virtual-link-domain {{ virtualLinkDomain }}

          //- Ticket Variants
          template(v-if="item.campaignType === 'Ticket' && (mainVariants.length || addonVariants.length)")
            h4.section-heading Select Tickets

            //- Main Ticket Tiers
            .variants-picker(v-if="mainVariants.length")
              .variant-card(
                v-for="variant in mainVariants"
                :key="variant.id"
                :class="{ 'variant-card--sold-out': availabilityMap[variant.id]?.soldOut }"
              )
                .variant-info
                  span.variant-title {{ variant.title }}
                  .variant-description(v-if="variant.description?.length") {{ variant.description }}
                  .variant-availability(v-if="availabilityMap[variant.id]")
                    span.badge-sold-out(v-if="availabilityMap[variant.id].soldOut") Sold Out
                    span.badge-remaining(
                      v-else-if="availabilityMap[variant.id].remaining !== null"
                    ) {{ availabilityMap[variant.id].remaining }} left
                .variant-row
                  .variant-stepper
                    button(
                      type="button"
                      @click.stop="decrementVariant(variant.id)"
                      :aria-label="`Decrease ${variant.title}`"
                    ).stepper-btn –
                    span.stepper-count {{ selectedVariantsAmounts[variant.id] || 0 }}
                    button(
                      type="button"
                      @click.stop="incrementVariant(variant.id)"
                      :disabled="availabilityMap[variant.id]?.soldOut"
                      :aria-label="`Increase ${variant.title}`"
                    ).stepper-btn +
                  .variant-cost(v-if="!variant.isFree")
                    .price-amount {{ formatPriceAmountWithFees(Number(variant.cost)) }}
                    .price-label incl. fees
                  .variant-cost.variant-free(v-else) Free

                .variant-included(v-if="variant.whatsIncluded")
                  .included-line(
                    v-for="line in variant.whatsIncluded.split('\\n')"
                    :key="line"
                  ) ✓ {{ line }}

                //- Legacy: Simple selection options dropdown (keep for backward compatibility)
                .variant-option-picker(
                  v-if="variant.selectionOptions && variant.selectionOptions.length && !variant.optionGroups"
                )
                  label.option-label Choose your option
                  el-select(
                    v-model="selectedVariantOptions[variant.id]"
                    @click.stop
                    placeholder="Select..."
                    :teleported="false"
                    popper-class="variant-option-popper"
                  ).option-select-el
                    el-option(
                      v-for="opt in variant.selectionOptions"
                      :key="opt"
                      :label="opt"
                      :value="opt"
                    )

                //- New: Advanced option groups with multi-select
                .variant-option-groups(
                  v-if="variant.optionGroups && variant.optionGroups.length"
                )
                  .option-group(
                    v-for="optionGroup in variant.optionGroups"
                    :key="optionGroup.id"
                  )
                    .option-group-header
                      span.option-group-title {{ optionGroup.title }}
                      span.option-required-badge(v-if="optionGroup.required") REQUIRED
                      span.option-multi-hint(v-if="optionGroup.multiSelect") (Select multiple)

                    .option-buttons-grid
                      button.option-button(
                        v-for="option in optionGroup.options"
                        :key="option.id"
                        @click.stop="handleOptionSelect(variant.id, optionGroup.id, option.id, optionGroup.multiSelect)"
                        :class="{ 'option-selected': isOptionSelected(variant.id, optionGroup.id, option.id) }"
                        type="button"
                      )
                        .option-checkbox
                          svg.check-icon(v-if="isOptionSelected(variant.id, optionGroup.id, option.id)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round")
                            polyline(points="20 6 9 17 4 12")
                        .option-content
                          span.option-name {{ option.name }}
                          .option-price(v-if="option.price === 0" class="option-price-free") Free
                          .option-price(v-else)
                            .price-amount {{ formatAddonPriceAmountWithFees(option.price) }}
                            .price-label incl. fees

            //- Combined Add-Ons Section (Ticket Tiers + Other Add-Ons)
          .combined-addons-wrapper(
            v-if="item.campaignType === 'Ticket' && (addonVariants.length || (item.addOns && item.addOns.length))"
          )
            h4.section-heading Add-Ons

            //- Add-On Ticket Tiers
            .variants-picker.addons-tier-section(v-if="addonVariants.length")
              .variant-card(
                v-for="variant in addonVariants"
                :key="variant.id"
                :class="{ 'variant-card--sold-out': availabilityMap[variant.id]?.soldOut }"
              )
                .variant-info
                  span.variant-title {{ variant.title }}
                  .variant-description(v-if="variant.description?.length") {{ variant.description }}
                  .variant-availability(v-if="availabilityMap[variant.id]")
                    span.badge-sold-out(v-if="availabilityMap[variant.id].soldOut") Sold Out
                    span.badge-remaining(
                      v-else-if="availabilityMap[variant.id].remaining !== null"
                    ) {{ availabilityMap[variant.id].remaining }} left
                .variant-row
                  .variant-stepper
                    button(
                      type="button"
                      @click.stop="decrementVariant(variant.id)"
                      :aria-label="`Decrease ${variant.title}`"
                    ).stepper-btn –
                    span.stepper-count {{ selectedVariantsAmounts[variant.id] || 0 }}
                    button(
                      type="button"
                      @click.stop="incrementVariant(variant.id)"
                      :disabled="availabilityMap[variant.id]?.soldOut"
                      :aria-label="`Increase ${variant.title}`"
                    ).stepper-btn +
                  .variant-cost(v-if="!variant.isFree")
                    .price-amount {{ formatPriceAmountWithFees(Number(variant.cost)) }}
                    .price-label incl. fees
                  .variant-cost.variant-free(v-else) Free

                .variant-included(v-if="variant.whatsIncluded")
                  .included-line(
                    v-for="line in variant.whatsIncluded.split('\\n')"
                    :key="line"
                  ) ✓ {{ line }}

                //- Legacy: Simple selection options dropdown (keep for backward compatibility)
                .variant-option-picker(
                  v-if="variant.selectionOptions && variant.selectionOptions.length && !variant.optionGroups"
                )
                  label.option-label Choose your option
                  el-select(
                    v-model="selectedVariantOptions[variant.id]"
                    @click.stop
                    placeholder="Select..."
                    :teleported="false"
                    popper-class="variant-option-popper"
                  ).option-select-el
                    el-option(
                      v-for="opt in variant.selectionOptions"
                      :key="opt"
                      :label="opt"
                      :value="opt"
                    )

                //- New: Advanced option groups with multi-select
                .variant-option-groups(
                  v-if="variant.optionGroups && variant.optionGroups.length"
                )
                  .option-group(
                    v-for="optionGroup in variant.optionGroups"
                    :key="optionGroup.id"
                  )
                    .option-group-header
                      span.option-group-title {{ optionGroup.title }}
                      span.option-required-badge(v-if="optionGroup.required") REQUIRED
                      span.option-multi-hint(v-if="optionGroup.multiSelect") (Select multiple)

                    .option-buttons-grid
                      button.option-button(
                        v-for="option in optionGroup.options"
                        :key="option.id"
                        @click.stop="handleOptionSelect(variant.id, optionGroup.id, option.id, optionGroup.multiSelect)"
                        :class="{ 'option-selected': isOptionSelected(variant.id, optionGroup.id, option.id) }"
                        type="button"
                      )
                        .option-checkbox
                          svg.check-icon(v-if="isOptionSelected(variant.id, optionGroup.id, option.id)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round")
                            polyline(points="20 6 9 17 4 12")
                        .option-content
                          span.option-name {{ option.name }}
                          .option-price(v-if="option.price === 0" class="option-price-free") Free
                          .option-price(v-else)
                            .price-amount {{ formatAddonPriceAmountWithFees(option.price) }}
                            .price-label incl. fees

            //- Other Add-Ons (Donation / Sweepstakes / Glow)
            .addons-section(v-if="item.addOns && item.addOns.length")
              .addon-card(v-for="addon in item.addOns" :key="addon.id")
                //- Type Badge
                .addon-type-badge(
                  v-if="addon.type"
                  :class="`addon-badge-${addon.type.toLowerCase()}`"
                ) {{ addon.type.toUpperCase() }}

                //- Donation add-on
                template(v-if="addon.type === 'Donation'")
                  .addon-title Support a Cause

                  //- Multi-charity selection
                  .donation-charities(v-if="addon.donationCauses && addon.donationCauses.length > 1")
                    .charities-header
                      .charities-title Select causes to support
                      .charities-hint (Each selected cause receives the full amount)
                    .charity-card(
                      v-for="(cause, idx) in addon.donationCauses"
                      :key="cause"
                      :class="{ 'charity-selected': isCharitySelected(addon.id, cause) }"
                    )
                      label.charity-label
                        input.charity-checkbox(
                          type="checkbox"
                          :checked="isCharitySelected(addon.id, cause)"
                          @change="toggleCharity(addon.id, cause)"
                          @click.stop
                        )
                        .charity-info
                          span.charity-name {{ getCauseName(cause) }}
                          span.charity-ein EIN: {{ getCauseEIN(cause) }}

                  //- Single charity (backward compatibility)
                  .addon-causes(v-else-if="addon.donationCauses && addon.donationCauses.length === 1")
                    .addon-cause-item(v-for="cause in addon.donationCauses" :key="cause") {{ cause }}

                  //- Amount selection (custom input is fallback when pricing is missing/invalid)
                  .addon-donation-fixed(v-if="getDonationPricingMode(addon) === 'fixed'")
                    span.addon-amount ${{ addon.donationFixed }}
                  .addon-donation-suggested(v-else-if="getDonationPricingMode(addon) === 'suggested'")
                    label.section-label Choose donation amount
                    .suggested-amounts
                      button(
                        v-for="amt in addon.donationSuggested"
                        :key="amt"
                        type="button"
                        :class="{ active: selectedAddOns[addon.id]?.donationAmount === amt }"
                        @click.stop="setAddonSuggestedAmount(addon.id, amt)"
                      ).suggested-btn ${{ amt }}
                    .custom-donation-input
                      label Or enter custom amount
                      .donation-input-wrapper
                        span.dollar-prefix $
                        input(
                          type="number"
                          min="1"
                          placeholder="0.00"
                          :value="customDonationInputs[addon.id] || ''"
                          @input="updateAddonDonationAmount(addon.id, $event.target.value)"
                          @click.stop
                        ).addon-input
                  .addon-donation-custom(v-else)
                    label.section-label Enter donation amount
                    .donation-input-wrapper
                      span.dollar-prefix $
                      input(
                        type="number"
                        min="1"
                        placeholder="0.00"
                        :value="customDonationInputs[addon.id] || ''"
                        @input="updateAddonDonationAmount(addon.id, $event.target.value)"
                        @click.stop
                      ).addon-input

                  //- Multi-cause donation summary
                  .donation-summary-message(v-if="addon.donationCauses && addon.donationCauses.length > 1 && getSelectedCharitiesCount(addon.id) > 1 && selectedAddOns[addon.id]?.donationAmount")
                    p.summary-message-text
                      | Your ${{ Number(selectedAddOns[addon.id]?.donationAmount).toFixed(2) }} donation will go to each of {{ getSelectedCharitiesCount(addon.id) }} selected causes (${{ (Number(selectedAddOns[addon.id]?.donationAmount) * getSelectedCharitiesCount(addon.id)).toFixed(2) }} total)

                  label.addon-checkbox-label
                    input(
                      type="checkbox"
                      :checked="selectedAddOns[addon.id]?.selected"
                      @change="updateAddonSelected(addon.id, $event.target.checked)"
                      @click.stop
                    )
                    span Add donation to my order

                //- Sweepstakes add-on
                template(v-else-if="addon.type === 'Sweepstakes'")
                  //- Loading skeleton while secondary fetch is in flight
                  .sweepstakes-loading(v-if="sweepstakesLinkedLoading")
                    .skeleton-line.skeleton-title
                    .skeleton-line.skeleton-short

                  //- Loaded content
                  template(v-else-if="getLinkedSweepstakes(addon)")
                    //- Title and Pricing
                    .sweepstakes-header
                      .addon-title.sweepstakes-title {{ getLinkedSweepstakes(addon).title }}
                      .sweepstakes-pricing(v-if="selectedAddOns[addon.id]?.sweepstakesEntries > 0")
                        .price-main ${{ calculateSweepstakesPrice(addon, selectedAddOns[addon.id]?.sweepstakesEntries || 0).toFixed(2) }}
                        .price-breakdown ({{ getEntriesBreakdown(selectedAddOns[addon.id]?.sweepstakesEntries || 0) }})

                    //- Description
                    .addon-description(v-if="addon.description") {{ addon.description }}

                    //- Prize Display Box
                    .sweepstakes-prize-box(
                      v-if="getLinkedSweepstakes(addon).sweepstakesSticker?.prizes || getLinkedSweepstakes(addon).sweepstakesSticker?.approximateRetailValue"
                    )
                      .prize-label Prize:
                      .prize-title(v-html="getLinkedSweepstakes(addon).sweepstakesSticker?.prizes || getLinkedSweepstakes(addon).sweepstakesSticker?.approximateRetailValue")
                      .prize-details(
                        v-if="getLinkedSweepstakes(addon).sweepstakesSticker?.approximateRetailValue && getLinkedSweepstakes(addon).sweepstakesSticker?.prizes"
                      ) {{ getLinkedSweepstakes(addon).sweepstakesSticker?.approximateRetailValue }}

                    //- Draw Date
                    .sweepstakes-dates-grid(v-if="getLinkedSweepstakes(addon).sweepstakesSticker?.drawingDateTime")
                      .date-column
                        .date-label Draw Date:
                        .date-value
                          | {{ formatDateTimeWithTimezone(getLinkedSweepstakes(addon).sweepstakesSticker.drawingDateTime, getLinkedSweepstakes(addon).sweepstakesSticker.drawingDateTimeTimezone) }}

                    //- Legal Disclaimers (before slider)
                    .sweepstakes-legal-box
                      p.legal-bold NO PURCHASE NECESSARY. Purchase will not increase chances of winning.
                      p.legal-text Must be 18+ and a legal US resident. Void where prohibited. Entry period ends at drawing date.
                      button.legal-rules-btn(
                        @click.stop="showSweepstakesRules = true"
                        type="button"
                      ) View Official Rules

                    //- Entry Slider
                    .sweepstakes-entry-slider
                      .slider-header
                        .slider-label Number of entries
                        .slider-value {{ selectedAddOns[addon.id]?.sweepstakesEntries || 0 }}
                      input.sweepstakes-range(
                        type="range"
                        min="0"
                        :max="getLinkedSweepstakes(addon).sweepstakesSticker?.entries?.length || 50"
                        :value="selectedAddOns[addon.id]?.sweepstakesEntries || 0"
                        @input="updateSweepstakesEntries(addon.id, $event.target.value)"
                        @click.stop
                      )
                      .slider-labels
                        span.slider-min 0
                        span.slider-max {{ getLinkedSweepstakes(addon).sweepstakesSticker?.entries?.length || 50 }}

                    //- Additional entries pricing below slider
                    .slider-footer-pricing(v-if="(selectedAddOns[addon.id]?.sweepstakesEntries || 0) >= 0")
                      | Additional entries: ${{ getSweepstakesEntryPrice(addon) }} each

                //- Glow add-on
                template(v-else-if="addon.type === 'Glow'")
                  .glow-addon-header
                    .glow-addon-info
                      .addon-title.glow-title {{ addon.glowName || 'Join the Glow Event' }}
                      .glow-price(v-if="!addon.glowIsFree && addon.glowPrice")
                        | {{ addon.glowPrice === 0 ? 'FREE' : `$${Number(addon.glowPrice).toFixed(2)}` }}
                      .glow-price.glow-free(v-else) FREE
                    button.glow-register-btn(
                      v-if="!selectedAddOns[addon.id]?.selected"
                      @click.stop="handleGlowAddonRegister(addon)"
                      type="button"
                    ) Register
                    button.glow-registered-btn(
                      v-else
                      type="button"
                      @click.stop="updateAddonSelected(addon.id, false)"
                    ) ✓ Registered
                  .addon-description(v-if="addon.description || addon.glowDescription") {{ addon.description || addon.glowDescription }}

          //- Non-ticket variants (keep old dropdown for non-ticket types)
          .variants-picker(v-if="item.varients?.length && item.campaignType !== 'Ticket'")
            .variant-card(
              v-for="variant in item.varients"
              :key="variant.id"
              :class="{ selected: selectedVariants.includes(variant.id) }"
              @click="onSelectVariant(variant.id)"
              @keydown.enter="onSelectVariant(variant.id)"
              @keydown.space="onSelectVariant(variant.id)"
              tabindex="0"
              role="button"
              :aria-label="`Select ${variant.title} variant`"
            )
              .variant-header
                span.variant-title {{ variant.title }}
                select(
                  v-model="selectedVariantsAmounts[variant.id]"
                  @click.stop="handleDropdownClick(variant.id)"
                  @change="handleDropdownChange(variant.id)"
                  :aria-label="`Quantity for ${variant.title}`"
                ).variant-select
                  option(value="0") 0
                  option(
                    v-for="amount in 100"
                    :key="amount"
                    :value="amount"
                  ) {{ amount }}

              .variant-cost(v-if="!variant.isFree")
                .price-amount {{ formatPriceAmountWithFees(Number(variant.cost)) }}
                .price-label incl. fees
              .variant-cost.variant-free(v-else) Free

              .variant-description(v-if="variant.description?.length") {{ variant.description }}

          //- Gallery Section
          .gallery-section-main(v-if="sortedGallery.length")
            h4.section-heading Gallery
            .gallery-grid
              .gallery-item(
                v-for="media in sortedGallery"
                :key="media.id"
                @click="openLightbox(media)"
              )
                img(
                  v-if="media.type === 'image'"
                  :src="media.thumbnail || media.url"
                  :alt="item.title"
                ).gallery-image
                video(
                  v-else-if="media.type === 'video'"
                  :src="media.url"
                ).gallery-video
                .gallery-item__zoom-hint
                  img(src="@/assets/icons/magnify.svg" alt="View")

          //- Lightbox
          transition(name="c-lightbox-fade")
            .c-lightbox(v-if="lightboxItem" @click.self="closeLightbox")
              .c-lightbox__toolbar
                button.c-lightbox__zoom-btn(type="button" @click="zoomOut" :disabled="lightboxZoom <= 0.5")
                  span −
                span.c-lightbox__zoom-label {{ Math.round(lightboxZoom * 100) }}%
                button.c-lightbox__zoom-btn(type="button" @click="zoomIn" :disabled="lightboxZoom >= 4")
                  span +
                button.c-lightbox__close(type="button" @click="closeLightbox") ×
              .c-lightbox__stage(@click.self="closeLightbox")
                video.c-lightbox__media(
                  v-if="lightboxItem.type === 'video'"
                  :src="lightboxItem.url"
                  controls
                  :style="{ transform: 'scale(' + lightboxZoom + ')' }"
                )
                img.c-lightbox__media(
                  v-else
                  :src="lightboxItem.url"
                  alt="Full size preview"
                  :style="{ transform: 'scale(' + lightboxZoom + ')' }"
                )

          //- FAQ Accordion Section (Inline — Ticket only)
          .faq-accordion-section(v-if="item.campaignType === 'Ticket' && sortedFaqs.length")
            .section-header
              h4.section-heading Frequently Asked Questions

            .faq-items
              .faq-item(
                v-for="(faq, index) in sortedFaqs"
                :key="faq.id"
                :class="{ 'faq-open': expandedFaqId === faq.id }"
              )
                button.faq-question-btn(
                  @click="toggleFaq(faq.id)"
                  type="button"
                )
                  span.faq-question-text {{ faq.question }}
                  img(
                    src="@/assets/icons/chevron-down.svg"
                    alt="Toggle"
                    :class="{ 'chevron-rotated': expandedFaqId === faq.id }"
                  ).faq-chevron
                .faq-answer-wrapper(v-if="expandedFaqId === faq.id")
                  .faq-answer {{ faq.answer }}

          //- Action Buttons Section (desktop only — fixed bar handles mobile)
          .action-buttons
            //- Post-add cart panel (desktop)
            .in-cart-panel(
              v-if="!isEmbedded && hasStickerInCart"
              :class="{ 'in-cart-panel--enter': inCartPanelEntering }"
            )
              .in-cart-row
                span.in-cart-summary In cart · {{ stickerCartItemCount }} · ${{ stickerCartSubtotal }}
                .in-cart-controls(
                  v-if="primaryStickerCartLine && (canAdjustStickerCartQuantity || canEditStickerCartPrice)"
                )
                  .in-cart-qty(v-if="canAdjustStickerCartQuantity")
                    button.in-cart-qty-btn(
                      type="button"
                      @click="decrementStickerCartQuantity"
                      aria-label="Decrease quantity"
                    ) –
                    span.in-cart-qty-value {{ getStickerCartLineQuantity(primaryStickerCartLine) }}
                    button.in-cart-qty-btn(
                      type="button"
                      @click="incrementStickerCartQuantity"
                      :disabled="getStickerCartLineQuantity(primaryStickerCartLine) >= maxCartQuantity"
                      aria-label="Increase quantity"
                    ) +
                  .in-cart-price(v-else-if="canEditStickerCartPrice")
                    span.in-cart-price-prefix $
                    input.in-cart-price-input(
                      type="text"
                      inputmode="decimal"
                      :value="getStickerCartPriceDisplay(primaryStickerCartLine)"
                      aria-label="Cart item price"
                      @input="onStickerCartPriceInput(primaryStickerCartLine.id, $event)"
                      @blur="commitStickerCartPrice(primaryStickerCartLine)"
                    )
              .in-cart-actions.in-cart-actions--compact
                button.in-cart-checkout(type="button" @click="goToCheckout") Checkout
                button.in-cart-continue(type="button" @click="continueShopping") Continue

            //- Ticket-specific: Add To Cart + Browse More Events
            template(v-if="item.campaignType === 'Ticket'")
              button(
                v-if="showStickerPrimaryCta"
                @click="onStickerActionButtonClick"
                :disabled="isButtonDisabled"
                :class="{ 'btn-add-to-cart--active': totalSelectedTickets > 0, 'button-loading': loading }"
                type="button"
              ).btn-add-to-cart
                span(v-if="!loading") {{ isEmbedded ? getTicketActionLabel() : `Add To Cart (${totalSelectedTickets})` }}
                span(v-else) Loading...
              button(
                v-if="!isEmbedded"
                @click="$router.push('/web/dashboard?filters=Ticket')"
                type="button"
              ).btn-browse-events Browse More Events
              button(
                v-if="isSignedIn && !isEventHost"
                @click="openMyTicketsDialog"
                type="button"
              ).btn-my-tickets
                svg.ticket-btn-icon(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                  path(d="M2 9a3 3 0 1 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 1 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z")
                  path(d="M13 5v2")
                  path(d="M13 17v2")
                  path(d="M13 11v2")
                span My Tickets
              button(
                v-if="isEventHost"
                @click="$router.push(`/web/event/${item.id}/checkin`)"
                type="button"
              ).btn-manage-checkin
                img(src="@/assets/icons/check.svg" alt="").checkin-btn-icon
                span Manage Check-In

            //- Non-ticket: Primary button + Secondary actions
            template(v-else)
              //- Embed: Donate + circular share icon inline
              .embed-action-row(v-if="isEmbedded")
                button(
                  @click="onStickerActionButtonClick"
                  :class="{ 'button-loading': loading, 'button-visually-disabled': isButtonDisabled, 'campaign-cta-highlight': campaignCtaHighlight }"
                  type="button"
                ).primary-action-button
                  span(v-if="!loading") {{ getButtonLabel() }}
                  span(v-else) Loading...
                button(@click="handleShare" type="button" aria-label="Share").embed-share-btn
                  img(src="@/assets/icons/share.svg" alt="")

              //- Non-embed: full-width primary
              button(
                v-else-if="showStickerPrimaryCta"
                @click="onStickerActionButtonClick"
                :class="{ 'button-loading': loading, 'button-visually-disabled': isButtonDisabled, 'campaign-cta-highlight': campaignCtaHighlight }"
                type="button"
              ).primary-action-button
                span(v-if="!loading") {{ getButtonLabel() }}
                span(v-else) Loading...

              //- Secondary Actions (Add to Shop, then Share / Save / Message Host)
              .secondary-actions(v-if="!isEmbedded")
                button(@click="handleAddToShop" type="button" :disabled="shopActionLoading").secondary-action-button.secondary-action-button--full
                  svg(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px;")
                    circle(cx="9" cy="21" r="1")
                    circle(cx="20" cy="21" r="1")
                    path(d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6")
                  span(v-if="!shopActionLoading") {{ isInShop ? 'Remove from Shop' : 'Add to Shop' }}
                  span(v-else) Loading...
                .secondary-actions-row
                  button(@click="handleShare" type="button").secondary-action-button
                    img(src="@/assets/icons/share.svg" alt="Share")
                    span Share
                  button(@click="handleToggleFavorite" type="button" :disabled="favoriteLoading" v-if="isSignedIn").secondary-action-button
                    svg(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" :fill="isFavorited ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px;" :style="{ color: isFavorited ? '#ef4444' : 'currentColor' }")
                      path(d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z")
                    span(v-if="!favoriteLoading") {{ isFavorited ? 'Saved' : 'Save' }}
                    span(v-else) Loading...
                  button(@click="showQuestionDialog = true" type="button" v-if="isSignedIn && item.campaignType === 'Ticket'").secondary-action-button
                    svg(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px;")
                      path(d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z")
                    span Message Host

          //- Floating Cart / CTA (Mobile Only)
          .cart-floating-action(v-if="showMobileFloatingAction")
            template(v-if="item.campaignType === 'Ticket'")
              .cart-floating-stack(v-if="!isEmbedded && hasStickerInCart")
                .cart-floating-in-cart(:class="{ 'cart-floating-in-cart--enter': inCartPanelEntering }")
                  .in-cart-row
                    span.in-cart-summary In cart · {{ stickerCartItemCount }} · ${{ stickerCartSubtotal }}
                  .in-cart-actions.in-cart-actions--compact
                    button.in-cart-checkout(type="button" @click="goToCheckout") Checkout
                    button.in-cart-continue(type="button" @click="continueShopping") Continue
              button.cart-floating-pill(
                v-if="showStickerPrimaryCta && totalSelectedTickets > 0"
                @click="onStickerActionButtonClick"
                :disabled="isButtonDisabled || loading"
                :class="{ 'is-loading': loading }"
                type="button"
              )
                span.pill-primary(v-if="!loading") {{ isEmbedded ? getButtonLabel() : 'Add to Cart' }}
                span.pill-primary(v-else) Loading...
                span.pill-secondary {{ totalSelectedTickets }} ticket{{ totalSelectedTickets > 1 ? 's' : '' }} · {{ formatPriceAmountWithFees(totalPrice) }}
            template(v-else)
              .cart-floating-row(v-if="isEmbedded")
                button.cart-floating-pill(
                  @click="onStickerActionButtonClick"
                  :class="{ 'is-loading': loading, 'button-visually-disabled': isButtonDisabled, 'campaign-cta-highlight': campaignCtaHighlight }"
                  type="button"
                )
                  span.pill-primary(v-if="!loading") {{ getButtonLabel() }}
                  span.pill-primary(v-else) Loading...
                button(@click="handleShare" type="button" aria-label="Share").embed-share-btn
                  img(src="@/assets/icons/share.svg" alt="")
              template(v-else)
                .cart-floating-stack(v-if="hasStickerInCart")
                  .cart-floating-in-cart(:class="{ 'cart-floating-in-cart--enter': inCartPanelEntering }")
                    .in-cart-row
                      span.in-cart-summary In cart · {{ stickerCartItemCount }} · ${{ stickerCartSubtotal }}
                      .in-cart-controls(
                        v-if="primaryStickerCartLine && (canAdjustStickerCartQuantity || canEditStickerCartPrice)"
                      )
                        .in-cart-qty(v-if="canAdjustStickerCartQuantity")
                          button.in-cart-qty-btn(
                            type="button"
                            @click="decrementStickerCartQuantity"
                            aria-label="Decrease quantity"
                          ) –
                          span.in-cart-qty-value {{ getStickerCartLineQuantity(primaryStickerCartLine) }}
                          button.in-cart-qty-btn(
                            type="button"
                            @click="incrementStickerCartQuantity"
                            :disabled="getStickerCartLineQuantity(primaryStickerCartLine) >= maxCartQuantity"
                            aria-label="Increase quantity"
                          ) +
                        .in-cart-price(v-else-if="canEditStickerCartPrice")
                          span.in-cart-price-prefix $
                          input.in-cart-price-input(
                            type="text"
                            inputmode="decimal"
                            :value="getStickerCartPriceDisplay(primaryStickerCartLine)"
                            aria-label="Cart item price"
                            @input="onStickerCartPriceInput(primaryStickerCartLine.id, $event)"
                            @blur="commitStickerCartPrice(primaryStickerCartLine)"
                          )
                    .in-cart-actions.in-cart-actions--compact
                      button.in-cart-checkout(type="button" @click="goToCheckout") Checkout
                      button.in-cart-continue(type="button" @click="continueShopping") Continue
                button.cart-floating-pill(
                  v-if="showStickerPrimaryCta"
                  @click="onStickerActionButtonClick"
                  :class="{ 'is-loading': loading, 'button-visually-disabled': isButtonDisabled, 'campaign-cta-highlight': campaignCtaHighlight }"
                  type="button"
                )
                  span.pill-primary(v-if="!loading") {{ getButtonLabel() }}
                  span.pill-primary(v-else) Loading...

          //- Sweepstakes Extended Details
          .sweepstakes-details(v-if="item.campaignType === 'Sweepstakes' && item.sweepstakesSticker")
            .detail-section(v-if="item.sweepstakesSticker.aboutTheCause")
              h6.detail-heading About The Cause
              .detail-content(v-html="item.sweepstakesSticker.aboutTheCause")

            .detail-section(v-if="item.sweepstakesSticker.numberOfPrizesAndWinners && item.sweepstakesSticker.numberOfPrizesAndWinners > 0")
              h6.detail-heading Number of Prizes and Winners
              .detail-content {{ item.sweepstakesSticker.numberOfPrizesAndWinners }}

            .detail-section(v-if="item.sweepstakesSticker.prizes")
              h6.detail-heading Prize
              .detail-content(v-html="item.sweepstakesSticker.prizes")

            .detail-section(v-if="item.sweepstakesSticker.nonProfitBeneficiary")
              h6.detail-heading Nonprofit Beneficiary
              .detail-content {{ item.sweepstakesSticker.nonProfitBeneficiary }}

            .detail-section(v-if="item.sweepstakesSticker.drawingDateTime")
              h6.detail-heading Drawing Date & Time
              .detail-content {{ formatDateTimeWithTimezone(item.sweepstakesSticker.drawingDateTime, item.sweepstakesSticker.drawingDateTimeTimezone) }}

            .detail-section(v-if="item.sweepstakesSticker.experienceDateTime")
              h6.detail-heading Experience Date & Time
              .detail-content {{ formatDateTimeWithTimezone(item.sweepstakesSticker.experienceDateTime, item.sweepstakesSticker.experienceDateTimeTimezone) }}

            .detail-section(v-if="item.location")
              h6.detail-heading Location of Experience
              .detail-content {{ item.location }}

            .detail-section(v-if="item.sweepstakesSticker.approximateRetailValue && item.sweepstakesSticker.approximateRetailValue > 0")
              h6.detail-heading Approximate Retail Value
              .detail-content ${{ item.sweepstakesSticker.approximateRetailValue }}

            //- Legal Sections
            .detail-section
              h6.detail-heading Age Restrictions
              .detail-content You must be 18 years old or older to enter and win. Sweepstakes is open to participants in the United States, except for places where sweepstakes or the awarding of a prize is prohibited. Subject to all applicable laws.

            .detail-section
              h6.detail-heading Eligible Countries
              .detail-content Open to legal residents in the United States only

            .detail-section
              h6.detail-heading Donation Transparency
              .detail-content *97.1/2.9 (For more information please see "Where does my donation go?" in the FAQs)

            .detail-section
              h6.detail-heading Selection Process
              .detail-content Grand Prize winner is selected using a computer generated selection method to ensure that each drawing is conducted completely at random. We will notify the winner via email and phone. The selected winner will have 24 hours to respond before a new winner is randomly selected.

            .detail-section
              h6.detail-heading Disclaimer
              .detail-content NO PURCHASE OR DONATION IS NECESSARY TO ENTER TO WIN. A PURCHASE OR DONATION WILL NOT IMPROVE YOUR CHANCES OF WINNING. VOID WHERE PROHIBITED. SEE OFFICIAL RULES FOR NON-DONATION METHOD OF ENTRY.

            .detail-section
              .detail-content See our Official Rules (United States) and Terms and Conditions for more details.

          //- Location (for non-sweepstakes, non-ticket — ticket has its own location display)
          .detail-section(
            v-if="item.location && item.campaignType !== 'Sweepstakes' && item.campaignType !== 'Ticket'"
          )
            h6.detail-heading Location
            .detail-content {{ item.location }}

          //- Waiver
          .detail-section(v-if="item.waiverURL && item.waiverURL.indexOf('http') >= 0")
            h6.detail-heading Waiver
            iframe(
              :src="item.waiverURL"
              :title="item.waiverURL"
              width="100%"
              height="250px"
            )

    //- Unpublished State
    div(v-else-if="item.published === false")
      .unpublished-message
        h6 Sorry, but this content is missing.
        div Good news! There are other fish in the sea. Check out the Marketplace for other great Stickers that want to be found by you.

    //- Loading State
    div(v-else-if="!error")
      .loading-message
        h6 "The two most powerful warriors are patience and time." - Leo Tolstoy
        div Hang tight! We are loading your content. If it fails to turn up, please reload the page in 5 seconds.

  //- Choose Charity Dialog
  el-dialog(
    v-model="isToChooseCause"
    title="Choose a Charity"
    :modal="true"
    :show-close="false"
    :lock-scroll="true"
    width="500px"
  ).charity-dialog
    img(src="@/assets/images/gift-solid.svg").charity-image
    div(v-if="item.isCausesSearchAllowed").mb-3
      VueMultiselect(
        v-model="selectedCause"
        :multiple="false"
        :taggable="false"
        :showNoOptions="false"
        :options="causeOptionsArray"
        :searchable="true"
        :clear-on-select="true"
        :close-on-select="true"
        :show-labels="false"
        :loading="causeLoading"
        :custom-label="customLabel"
        placeholder="Search for Charity"
        @search-change="getCausesDropdownOptions"
        @select="searchSelected"
      )
    .charity-section-label Creator's Suggestions
    .charity-suggestions
      el-checkbox-group(v-model="selectedCauseIds").charity-checkbox-group
        div(
          v-for="(cause, index) in item.causes"
          :key="index"
        )
          div(v-if="cause !== undefined").charity-row
            el-checkbox(
              :label="cause.split(' : ')[1]"
            ).charity-suggestion
              | {{ cause.split(' : ')[0] }}
          hr(v-if="index < item.causes.length - 1 && cause !== undefined").hr-color
    .charity-selection-note(v-if="selectedCauseIds.length > 1")
      | Each charity will receive ${{ (customAmount || item.cost || 0).toFixed(2) }}
      br
      | Total charge: ${{ ((customAmount || item.cost || 0) * selectedCauseIds.length).toFixed(2) }}
    template(#footer)
      button(@click="onStickerActionButtonClick").charity-continue-btn Continue

  DialogCartDonationAmount(
    v-model="showCartAmountDialog"
    :items="cartAmountDialogItems"
    @confirm="handleCartAmountDialogConfirm"
  )

  //- Login Dialog
  el-dialog(
    v-model="showLoginDialog"
    title="Sign in required"
    :show-close="true"
    width="340px"
  ).login-dialog.text-center
    .login-dialog-content
      p To purchase this sticker, you need to sign in or create an account.
      .login-buttons
        el-button(type="success" @click="goToSignUp").w-100.mb-2.primary-btn Sign Up
        el-button(@click="goToSignIn").w-100.mx-0.secondary-btn Sign In

  //- Sweepstakes Already Entered Dialog
  el-dialog(
    v-model="showSweepstakesAlreadyEnteredDialog"
    title="Already Entered"
    :show-close="true"
    width="340px"
  ).sweepstakes-dialog.text-center
    .sweepstakes-dialog-content
      p You've already entered this sweepstakes.
      b Good luck!
      .sweepstakes-buttons
        el-button(type="success" @click="showSweepstakesAlreadyEnteredDialog = false").w-100.primary-btn OK

  //- Share Modal
  el-dialog(
    v-model="showShareModal"
    title="Share"
    :show-close="true"
    width="400px"
  ).share-modal
    .share-modal-content
      //- QR Code Section
      .qr-code-section
        qrcode-vue(:value="getStickerUrl()" :size="200")

      //- Copy URL Button
      .share-option(@click="copyUrlToClipboard(); closeShareModal()")
        .share-option-icon
          svg(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px; color: white;")
            rect(x="9" y="9" width="13" height="13" rx="2" ry="2")
            path(d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1")
        .share-option-text Copy URL

      //- Native share (email, Telegram, etc.)
      .share-option(@click="shareViaSystem(); closeShareModal()")
        .share-option-icon
          img(src="@/assets/icons/share.svg" alt="Share")
        .share-option-text Share To

  //- Ask Host Question Dialog
  el-dialog(
    v-model="showQuestionDialog"
    title="Message Host"
    width="500px"
    @close="resetQuestionForm"
  )
    el-form(ref="questionFormRef" :model="questionForm" :rules="questionRules" label-position="top")
      el-form-item(label="Your Question" prop="question")
        el-input(
          v-model="questionForm.question"
          type="textarea"
          :rows="6"
          placeholder="Ask the host a question about this event"
          maxlength="2000"
          show-word-limit
        )
      el-form-item(label="Your Name (optional)" prop="name")
        el-input(
          v-model="questionForm.name"
          placeholder="Enter your name"
          maxlength="100"
        )
      el-form-item(label="Your Email (optional)" prop="email")
        el-input(
          v-model="questionForm.email"
          placeholder="Enter your email"
          type="email"
        )
    template(#footer)
      el-button(@click="showQuestionDialog = false") Cancel
      el-button(type="primary" @click="handleSendQuestion" :loading="sendingQuestion") Send Question

</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, onUnmounted, reactive, ref, watch, nextTick,
} from 'vue';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import VueMultiselect from 'vue-multiselect';
import VueSlider from 'vue-3-slider-component';
import { ElMessage } from 'element-plus';
import QrcodeVue from 'qrcode.vue';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import gqlRequest from '../dashboard/gql-request';
import SmartBanner from './SmartBanner.vue';
import isUUID from '@/utils/isUUID';
import { confirmRemoveCartItem } from '@/utils/cartConfirm';
import {
  addEmbeddedContextToUrl,
  captureEmbedOrganizationContext,
  getEmbedOrganizationHomeUrl,
  isEmbeddedContext,
  isEmbeddedExperience,
  navigateToEmbedOrganizationHome,
  requestEmbedHeightSync,
  resolveEmbeddedPaymentUrl,
  prepareEmbedPaymentNavigation,
} from '@/utils/embeddedUtils';
import { addToCalendarClick } from '@/utils/calendarUtils';
import { getCreatorProfilePath } from '@/utils/profileNavigation';
import { navigateToCheckout } from '@/utils/checkoutNavigation';
import { useStickerHostCheck } from '@/composables/useStickerHostCheck';
import { showWebAppSuccessBanner } from '@/composables/useWebAppSuccessBanner';
import { formatDateTimeWithTimezone, formatDate, formatTime } from '@/utils/dateUtils';
import { 
  calculatePriceWithFees, 
  formatPriceWithFees, 
  formatPriceRangeWithFees, 
  formatAddonPriceWithFees,
  formatPriceAmountWithFees,
  formatPriceRangeAmountWithFees,
  formatAddonPriceAmountWithFees,
} from '@/utils/priceWithFees';
import { makeRequest } from '@/graphs/index';
import {
  mutateSocialShopSticker,
  mutateRemoveSocialShopSticker,
  findPublishedStickersShortDetailsWithSocialShop,
  findTicketAvailability,
  findStickerAnnouncements,
  askStickerHost,
  trackSocialShopStickerView,
  trackStickerView,
  trackStickerShare,
} from '@/graphs/gql';
import { formatCountNoDecimal } from '@/views/web-app/glow/utils/numberFormatting';
import DialogCartDonationAmount from '@/views/web-app/sticker/DialogCartDonationAmount.vue';
import {
  buildCartAmountDialogItem,
  shouldPromptCartAmountBeforeAdd,
  stickerHasInlineCartAmount,
  CartAmountConfirmEntry,
} from '@/utils/cartItemAmount';
import type { CartLine } from '@/store/modules/cart';

interface TicketTierAvailability {
  variantId: string;
  title: string;
  isAddOn: boolean;
  capacity: number | null;
  issued: number;
  reserved: number;
  remaining: number | null;
  soldOut: boolean;
}

interface IssuedTicket {
  id: string;
  code: string;
  tierName: string;
  selectedOption?: string;
  stickerId: string;
  variantId: string;
  section?: string;
  row?: string;
  seat?: string;
  checkedInAt?: string;
  createdAt: string;
}

dayjs.extend(utc);
dayjs.extend(advancedFormat);

interface Cause {
  name: string;
  id: string;
}

interface Item {
    id: number | null;
    title: string;
    description: string;
    url: string;
    slug?: string;
    published: any;
    tags: string[];
    cost: number;
    campaignType: null | string;
    causes: string[];
    maxQuantity: number;
    maxDonation: null | number;
    donationMultiplier: number;
    isCausesSearchAllowed: boolean;
    quickCosts?: any[];
    user?: any;
    organization?: any;
    varients?: any;
    sweepstakesSticker:any;
    startDate?: any;
    endDate?: any;
    startDateTimeZone?: any;
    endDateTimeZone?: any;
    eventDate?: any;
    eventDateTimeZone?: string;
    location?: any;
    waiverURL?: any;
    impact: any;
    creatorTitle: any;
    isFeatured: boolean;
    priceRange?: string | null;
    bannerUrl?: string | null;
    locationType?: 'physical' | 'virtual' | null;
    virtualLink?: string | null;
    hideLocation?: boolean;
    glow?: {
      id: string;
      slug?: string;
      title: string;
      glowStartDate: string;
      glowEndDate: string;
      glowStartDateTimeZone?: string;
      glowEndDateTimeZone?: string;
      description: string;
    } | null;
    addOns?: Array<{
      id: string;
      type: 'Donation' | 'Sweepstakes' | 'Glow';
      sortOrder: number;
      donationCauses?: string[];
      donationPricing?: 'fixed' | 'suggested' | 'custom';
      donationFixed?: number;
      donationSuggested?: number[];
      sweepstakesStickerId?: string;
      glowIsExisting?: boolean;
      glowId?: string;
      glowName?: string;
      glowDescription?: string;
      glowIsFree?: boolean;
      glowPrice?: number;
    }>;
    gallery?: Array<{
      id: string;
      type: 'image' | 'video';
      url: string;
      thumbnail?: string;
      sortOrder: number;
    }>;
    faqs?: Array<{
      id: string;
      question: string;
      answer: string;
      sortOrder: number;
    }>;
    entity?: string;
    entityId?: string;
    viewCount?: number;
    shareCount?: number;
}

class CauseItem {
  public value: string;

  public label: string;

  constructor(value: string, label: string) {
    this.value = value;
    this.label = label;
  }
}

export default defineComponent({
  name: 'WebAppSticker',
  components: {
    WebAppLayout,
    VueMultiselect,
    SmartBanner,
    VueSlider,
    QrcodeVue,
    DialogCartDonationAmount,
  },
  setup() {
    const {
      route,
      router,
      getSticker,
      getStickerBySlug,
      getPublicSticker,
      getPublicStickerBySlug,
      doCreateStickerWebIntent,
      doCreateStickerWebIntentPublic,
      checkIsStickerPurchased,
      getCuratedBenevityCauses,
      getcausesAsDropdown,
      toggleFavorite,
      getMyFavorites,
      fetchUserOrganizations,
    } = gqlRequest();

    const { slugorid } = route.params;
    const store = useStore();
    const isEmbedded = computed(() => isEmbeddedExperience({
      path: route.path,
      query: route.query,
    }));
    const isStandaloneEmbed = computed(() => isEmbedded.value && (
      route.query.standalone === '1'
      || route.query.standalone === 'true'
    ));
    const selectedVariantsAmounts = ref<{ [key: string]: number }>({});
    const selectedVariants = ref<string[]>([]);
    const selectedVariantOptions = ref<{ [variantId: string]: string }>({});
    // New: Advanced option groups support (variantId -> optionGroupId -> [selected option IDs])
    const selectedOptionGroups = ref<Record<string, Record<string, string[]>>>({});
    const selectedAddOns = ref<{
      [addOnId: string]: {
        selected: boolean;
        donationAmount?: number;
        donationCauseId?: string;
        charities?: { [charityId: string]: number }; // Multi-charity support: selected cause IDs
        sweepstakesEntries?: number;
      }
    }>({});
    // Tracks the raw text inside the "Or enter custom amount" field per addon,
    // independent of donationAmount so the field never clears mid-typing when an
    // intermediate value happens to match one of the preset suggested amounts.
    const customDonationInputs = ref<{ [addonId: string]: string }>({});

    // Keyed by sweepstakesStickerId — populated via secondary fetch after item loads
    const sweepstakesLinkedStickers = ref<{ [stickerId: string]: any }>({});
    const sweepstakesLinkedLoading = ref(false);
    const causeOptionsArray = ref<any[]>([]);
    const causeOptions = ref<any[]>([]);
    const isPromptDownloadApp = ref(false);
    const isStickerPurchased = ref(false);
    const isToChooseCause = ref(false);
    const showCartAmountDialog = ref(false);
    const cartAmountDialogItems = ref<ReturnType<typeof buildCartAmountDialogItem>[]>([]);
    const causeLoading = ref(false);
    const loading = ref(false);
    const selectedCauseIds = ref<string[]>([]);
    const firstCauseName = ref('');
    const selectedCause = ref('');
    const customAmount = ref();
    const amountInputWrapperRef = ref<HTMLElement | null>(null);
    const amountInputDesktopWrapperRef = ref<HTMLElement | null>(null);
    const amountInputHighlight = ref(false);
    const campaignCtaHighlight = ref(false);
    const value = ref(0);
    const showLoginDialog = ref(false);
    const showSweepstakesAlreadyEnteredDialog = ref(false);
    const showSweepstakesRules = ref(false);
    const error = ref<string | null>(null);
    const activeTab = ref('Details');
    const expandedFaqId = ref<string | null>(null);
    const shopActionLoading = ref(false);
    const userShopStickerIds = ref<Set<string>>(new Set());
    const showShareModal = ref(false);
    const favoriteLoading = ref(false);
    const userFavoritedStickerIds = ref<Set<string>>(new Set());
    const {
      hostOrganizationIds,
      managedOrganizationIds,
      hostCheckReady,
      hostContextTick,
      hostOrganizationsLoadMeta,
      loadHostOrganizationIds,
      finalizeHostCheck,
      isHostOfSticker,
    } = useStickerHostCheck(store, fetchUserOrganizations);
    const announcementsLoading = ref(false);
    const stickerAnnouncements = ref<any[]>([]);
    const showQuestionDialog = ref(false);
    const sendingQuestion = ref(false);
    const questionFormRef = ref();

    const questionForm = ref({
      question: '',
      name: '',
      email: '',
    });

    const questionRules = {
      question: [
        { required: true, message: 'Please enter your question', trigger: 'blur' },
        { max: 2000, message: 'Question must be 2000 characters or less', trigger: 'blur' },
      ],
      email: [
        { type: 'email', message: 'Please enter a valid email', trigger: 'blur' },
      ],
    };
    const availability = ref<TicketTierAvailability[]>([]);
    const availabilityMap = computed(() => {
      const map: Record<string, TicketTierAvailability> = {};
      availability.value.forEach((t) => { map[t.variantId] = t; });
      return map;
    });

    async function fetchAvailability(stickerId: string) {
      try {
        const response = await makeRequest('query', findTicketAvailability, { stickerId }, null);
        if (response?.data?.findTicketAvailability) {
          availability.value = response.data.findTicketAvailability;
        }
      } catch (e) {
        console.error('fetchAvailability error:', e);
      }
    }

    // Extract socialShopId, glowId, and returnTo from query parameters
    const socialShopId = computed(() => route.query.socialShopId as string);
    const glowId = computed(() => route.query.glowId as string);
    const returnTo = computed(() => route.query.returnTo as string);

    // Creator computed properties
    const creatorName = computed(() => {
      if (item.organization?.name) {
        return item.organization.name.toUpperCase();
      }
      if (item.user?.firstname && item.user?.lastname) {
        return `${item.user.firstname} ${item.user.lastname}`.toUpperCase();
      }
      return 'CREATOR';
    });

    const creatorInitial = computed(() => {
      if (item.organization?.name) {
        return item.organization.name.charAt(0).toUpperCase();
      }
      if (item.user?.firstname) {
        return item.user.firstname.charAt(0).toUpperCase();
      }
      return 'C';
    });

    const creatorProfilePath = computed(() => getCreatorProfilePath(
      item.entity,
      item.organization,
      item.user,
    ));

    const handleCreatorClick = () => {
      if (creatorProfilePath.value) {
        router.push(creatorProfilePath.value);
      }
    };

    const normalizedVirtualLink = computed(() => {
      const link = item.virtualLink;
      if (!link) return '';
      if (/^https?:\/\//i.test(link)) return link;
      return `https://${link}`;
    });

    const virtualLinkDomain = computed(() => {
      try {
        const url = new URL(normalizedVirtualLink.value);
        return url.hostname.replace(/^www\./, '');
      } catch {
        return normalizedVirtualLink.value;
      }
    });

    // Helper functions for charity info
    const getCauseName = (cause: string) => {
      if (cause && cause.includes(' : ')) {
        return cause.split(' : ')[0];
      }
      return cause;
    };

    const getCauseEIN = (cause: string) => {
      if (cause && cause.includes(' : ')) {
        const parts = cause.split(' : ');
        return parts[1] || '12-3456789';
      }
      return '12-3456789';
    };

    // Button disabled state
    const isButtonDisabled = computed(() => {
      if (item.campaignType === 'Ticket' && selectedVariants.value.length === 0) {
        return true;
      }
      const missingOption = selectedVariants.value.some((variantId) => {
        const variant = item.varients?.find((v) => v.id === variantId);
        return variant?.selectionOptions?.length && !selectedVariantOptions.value[variantId];
      });
      if (missingOption) return true;
      if (item.quickCosts?.length && !(customAmount.value >= 0)
          && (item.campaignType === 'Campaign' || item.campaignType === 'campaign' || !item.campaignType)) {
        return true;
      }
      return false;
    });

    const maxValue = 10;

    const item = reactive<Item>({
      id: null,
      title: '',
      description: '',
      url: '',
      published: null,
      tags: [],
      cost: 0,
      campaignType: null,
      causes: [''],
      maxQuantity: 0,
      maxDonation: null,
      donationMultiplier: 1,
      impact: null,
      startDate: null,
      endDate: null,
      startDateTimeZone: null,
      endDateTimeZone: null,
      sweepstakesSticker: {
        aboutTheCause: null,
        approximateRetailValue: null,
        checkoutPageLegal: null,
        createdAt: null,
        drawingDateTime: null,
        drawingDateTimeTimezone: null,
        entries: [null],
        entryCost: [null],
        experienceDateTime: null,
        experienceDateTimeTimezone: null,
        nonProfitBeneficiary: null,
        numberOfPrizesAndWinners: null,
        prizes: null,
        sweepstakesConfirmation: null,
        sweepstakesLoserMessage: null,
        sweepstakesWinnerMessage: null,
      },
      user: {
        firstname: '',
        lastname: '',
        avatar: null,
      },
      organization: { name: '', avatar: null },
      creatorTitle: null,
      isFeatured: false,
      isCausesSearchAllowed: false,
      priceRange: null,
      bannerUrl: null,
      locationType: null,
      virtualLink: null,
      hideLocation: false,
      addOns: [],
      gallery: [],
      faqs: [],
    });

    // SWEEPSTAKES SLIDER
    function sortEntriesAndCosts() {
      const entriesCopy = [...item.sweepstakesSticker.entries];
      const entryCostCopy = [...item.sweepstakesSticker.entryCost];

      const sortedIndices = entriesCopy.map((value, index) => ({ value, index }))
        .sort((a, b) => a.value - b.value)
        .map((sorted) => sorted.index);

      item.sweepstakesSticker.entries = sortedIndices.map((i) => entriesCopy[i]);
      item.sweepstakesSticker.entryCost = sortedIndices.map((i) => entryCostCopy[i]);
    }

    const currentEntryText = computed(() => {
      if (value.value === 0) {
        return '1 Entry'; // Default value when slider is at index 0
      }
      return `${item.sweepstakesSticker.entries[value.value - 1]} Entries`;
    });

    const currentCostText = computed(() => {
      if (value.value === 0) {
        item.cost = 0;
        customAmount.value = 0;
        return 'Free'; // Default value when slider is at index 0
      }
      customAmount.value = item.sweepstakesSticker.entryCost[value.value - 1] ?? 0;
      return `Donate $${item.sweepstakesSticker.entryCost[value.value - 1]}`;
    });

    const marks = computed(() => {
      const marks = {};
      const { length } = item.sweepstakesSticker.entries;

      for (let i = 0; i <= length; i++) {
        marks[i] = '';
      }
      return marks;
    });

    const selectValue = (index: number) => {
      value.value = index;
    };

    // SWEEPSTAKES SLIDER END

    const incrementVariant = (id: string) => {
      const current = selectedVariantsAmounts.value[id] || 0;
      selectedVariantsAmounts.value[id] = current + 1;
      if (!selectedVariants.value.includes(id)) {
        selectedVariants.value.push(id);
      }
    };

    const decrementVariant = (id: string) => {
      const current = selectedVariantsAmounts.value[id] || 0;
      if (current <= 0) return;
      selectedVariantsAmounts.value[id] = current - 1;
      if (current - 1 === 0) {
        const idx = selectedVariants.value.indexOf(id);
        if (idx > -1) selectedVariants.value.splice(idx, 1);
      }
    };

    // Handle option group selection (multi-select or single-select)
    const handleOptionSelect = (
      variantId: string,
      optionGroupId: string,
      optionId: string,
      multiSelect: boolean,
    ) => {
      if (!selectedOptionGroups.value[variantId]) {
        selectedOptionGroups.value[variantId] = {};
      }

      const currentSelections = selectedOptionGroups.value[variantId][optionGroupId] || [];

      if (multiSelect) {
        // Toggle in array for multi-select
        if (currentSelections.includes(optionId)) {
          selectedOptionGroups.value[variantId][optionGroupId] = currentSelections.filter((id) => id !== optionId);
        } else {
          selectedOptionGroups.value[variantId][optionGroupId] = [...currentSelections, optionId];
        }
      } else {
        // Replace with single selection
        selectedOptionGroups.value[variantId][optionGroupId] = [optionId];
      }
    };

    // Check if option is selected
    const isOptionSelected = (variantId: string, optionGroupId: string, optionId: string): boolean => {
      const selections = selectedOptionGroups.value[variantId]?.[optionGroupId] || [];
      return selections.includes(optionId);
    };

    // Get total price of selected options for a variant
    const getOptionsTotalPrice = (variantId: string): number => {
      let total = 0;
      const variant = item.varients?.find((v: any) => v.id === variantId);
      if (!variant?.optionGroups) return 0;

      for (const optionGroup of variant.optionGroups) {
        const selectedIds = selectedOptionGroups.value[variantId]?.[optionGroup.id] || [];
        for (const optionId of selectedIds) {
          const option = optionGroup.options.find((o: any) => o.id === optionId);
          if (option) total += option.price;
        }
      }
      return total;
    };

    const onSelectVariant = (id: string) => {
      const index = selectedVariants.value.indexOf(id);
      if (index > -1) {
        selectedVariants.value.splice(index, 1);
        // return delete selectedVariantsAmounts.value[id];
        return selectedVariantsAmounts.value[id] = 0;
      }
      selectedVariantsAmounts.value[id] = 1;
      selectedVariants.value.push(id);
    };

    const handleDropdownClick = (variantId: string) => {
      // If variant is not selected yet, select it automatically when dropdown is clicked
      if (!selectedVariants.value.includes(variantId)) {
        selectedVariants.value.push(variantId);
        // Set default amount to 1 if not set
        if (!selectedVariantsAmounts.value[variantId]) {
          selectedVariantsAmounts.value[variantId] = 1;
        }
      }
    };

    const handleDropdownChange = (variantId: string) => {
      const quantity = selectedVariantsAmounts.value[variantId];

      if (quantity > 0) {
        // If quantity is greater than 0, ensure variant is selected
        if (!selectedVariants.value.includes(variantId)) {
          selectedVariants.value.push(variantId);
        }
      } else {
        // If quantity is 0, remove variant from selection
        const index = selectedVariants.value.indexOf(variantId);
        if (index > -1) {
          selectedVariants.value.splice(index, 1);
        }
      }
    };

    async function getCausesDropdownOptions(query: string) {
      if (query) {
        causeLoading.value = true;
        const benevityCauses: Array<Cause> = await getcausesAsDropdown(query);
        setTimeout(() => {
          causeLoading.value = false;
          causeOptions.value = [];
          benevityCauses.forEach((element) => {
            const data = new CauseItem(
              `${element.name} : ${element.id}`,
              `${element.name} : ${element.id}`,
            );
            causeOptions.value.push(data);
            causeOptionsArray.value.push(`${element.name} : ${element.id}`);
          });
        }, 200);
      } else {
        causeOptions.value = [];
      }
    }
    function customLabel(option: string) {
      return option.split(' : ')[0];
    }

    function handleCauseChange() {
      selectedCause.value = '';
      causeOptionsArray.value = [];
    }

    function searchSelected() {
      selectedCauseIds.value = [];
    }

    function getCausesList() {
      let firstCause = '';
      if (item.causes.length) {
        // eslint-disable-next-line array-callback-return
        item.causes.map((x, index) => {
          if (index === 0) {
            firstCause = `${firstCause} ${x.split(' : ')[0]}`;
          } else {
            firstCause = `${firstCause}, ${x.split(' : ')[0]}`;
          }
        });
      }
      return firstCause;
    }

    // Button label logic based on the provided table
    function getButtonLabel() {
      const campaignType = item.campaignType?.toLowerCase();

      if (isEmbedded.value) {
        if (campaignType === 'campaign' || campaignType === 'matchcampaign') {
          if (item.causes?.length > 1 && selectedCauseIds.value.length === 0) {
            return 'Choose a Charity';
          }
          return 'Donate';
        }

        if (campaignType === 'sweepstakes') {
          return 'Enter to Win';
        }

        if (campaignType === 'ticket') {
          return 'Register';
        }

        if (item.cost === 0 && !item.varients?.length && !item.sweepstakesSticker && !item.quickCosts?.length) {
          return 'Add to Sticker Wall';
        }

        if (item.quickCosts && item.quickCosts.length > 0) {
          if (item.causes?.length > 0 || campaignType === 'campaign') {
            return item.causes?.length > 1 && selectedCauseIds.value.length === 0
              ? 'Choose a Charity'
              : 'Donate';
          }
          return 'Purchase';
        }

        if (item.cost > 0 || item.varients?.length || (item.sweepstakesSticker && customAmount.value > 0)) {
          return 'Purchase';
        }

        return 'Purchase';
      }

      // Handle Campaign types
      if (campaignType === 'campaign' || campaignType === 'matchcampaign') {
        if (item.causes?.length > 1 && selectedCauseIds.value.length === 0) {
          return 'Choose a Charity';
        }
        return 'Add to Cart';
      }

      // Handle Sweepstakes
      if (campaignType === 'sweepstakes') {
        return 'Add to Cart';
      }

      // Handle Tickets
      if (campaignType === 'ticket') {
        return 'Register';
      }

      // Handle Free Stickers (only truly free with no payment options)
      if (item.cost === 0 && !item.varients?.length && !item.sweepstakesSticker && !item.quickCosts?.length) {
        return 'Add to Sticker Wall';
      }

      // Handle items with quickCosts (custom donation amounts)
      if (item.quickCosts && item.quickCosts.length > 0) {
        // If it has causes or is explicitly a campaign, use cart flow
        if (item.causes?.length > 0 || campaignType === 'campaign') {
          return item.causes?.length > 1 && selectedCauseIds.value.length === 0 ? 'Choose a Charity' : 'Add to Cart';
        }
        // Otherwise it's a purchase
        return 'Add to Cart';
      }

      // Handle other paid scenarios
      if (item.cost > 0 || item.varients?.length || (item.sweepstakesSticker && customAmount.value > 0)) {
        return 'Add to Cart';
      }

      // Default fallback
      return 'Add to Cart';
    }

    async function getItem() {
      try {
        let sticker: any = null;
        const usePublicAccess = !store.getters['users/isMeSignedIn'] || isEmbedded.value;
        if (slugorid) {
          if (isUUID(slugorid)) {
            // Try by ID first
            sticker = usePublicAccess
              ? await getPublicSticker(slugorid)
              : await getSticker(slugorid);
            if (!sticker) {
              // If not found, try by slug
              sticker = usePublicAccess
                ? await getPublicStickerBySlug(slugorid)
                : await getStickerBySlug(slugorid);
            }
          } else {
            // Not a UUID, treat as slug
            sticker = usePublicAccess
              ? await getPublicStickerBySlug(slugorid)
              : await getStickerBySlug(slugorid);
          }
        }
        if (!sticker) {
          error.value = 'Sticker not found';
          return;
        }
        Object.assign(item, sticker);
        item.causes = (sticker.causes !== null && sticker.causes !== '')
          ? sticker.causes.split(' |=| ')
          : [];
        getCausesList();
        if (!item.causes.length && (item.campaignType === 'Campaign' || item.campaignType === 'Sweepstakes')) {
          const curatedCauses: string[] = [];
          const curatedBenevityCauses = await getCuratedBenevityCauses();
          curatedBenevityCauses.forEach((curatedCause) => {
            curatedCauses.push(`${curatedCause.name} : ${curatedCause.id}`);
          });
          Object.assign(item.causes, curatedCauses);
        }
        if (item.varients && Array.isArray(item.varients)) {
          item.varients.forEach((variant) => {
            selectedVariantsAmounts.value[variant.id] = 0;
            if (variant.selectionOptions && variant.selectionOptions.length > 0) {
              selectedVariantOptions.value[variant.id] = variant.selectionOptions[0];
            }
          });
        }
        if (item.addOns && Array.isArray(item.addOns)) {
          item.addOns.forEach((addon: any) => {
            if (addon.id && !selectedAddOns.value[addon.id]) {
              selectedAddOns.value[addon.id] = { selected: false };
            }
          });
          // Fetch full sticker data for each sweepstakes add-on using findSticker (full fragment)
          const sweepstakesIds: string[] = item.addOns
            .filter((a: any) => a.type === 'Sweepstakes' && a.sweepstakesStickerId)
            .map((a: any) => a.sweepstakesStickerId);
          if (sweepstakesIds.length > 0) {
            sweepstakesLinkedLoading.value = true;
            try {
              const results = await Promise.all(
                sweepstakesIds.map((id: string) => getPublicSticker(id)),
              );
              results.forEach((s: any) => {
                if (s?.id) sweepstakesLinkedStickers.value[s.id] = s;
              });
            } catch (fetchErr) {
              console.error('[Sweepstakes] Failed to fetch linked sweepstakes stickers:', fetchErr);
            } finally {
              sweepstakesLinkedLoading.value = false;
            }
          }
        }
        if (item.sweepstakesSticker && item.sweepstakesSticker.entries != null) {
          sortEntriesAndCosts();
          // Set default slider value to cheapest paid option (skip free if it exists)
          // Find first non-zero entry cost index
          const firstPaidIndex = item.sweepstakesSticker.entryCost.findIndex((cost) => cost > 0);
          // If there's a paid option, use it; otherwise default to index 1
          value.value = firstPaidIndex >= 0 ? firstPaidIndex + 1 : 1;
        }
      } catch (e) {
        console.error('Error in getItem:', e);
        error.value = 'Failed to load sticker data';
      }
    }

    function addUserToUrl(url) {
      const urlObj = new URL(url, window.location.origin);
      const params = new URLSearchParams(urlObj.search);

      params.set('isWebUser', 'true');

      // Add returnTo parameter if it exists (for proper navigation after purchase)
      if (returnTo.value) {
        params.set('returnTo', returnTo.value);
      }

      urlObj.search = params.toString();

      return urlObj.toString();
    }

    async function doCheckIsStickerPurchased() {
      if (store.getters['users/me'] !== null) {
        const isStickerPurchasedResponse = await checkIsStickerPurchased('User', store.getters['users/me'].id, item.id);
        isStickerPurchased.value = isStickerPurchasedResponse;
        // Note: Removed the line that was setting this to false, which was overriding the actual response
      }
    }

    function getRedirectUrl() {
      return import.meta.env.VITE_WEB_BASE_URL;
    }

    function goToSignUp() {
      router.push(`/auth/web/signup?stickerId=${item.id}`);
    }

    function goToSignIn() {
      router.push(`/auth/web/signin?stickerId=${item.id}`);
    }

    function handleCollectBadgeClick() {
      // Validate before proceeding
      if (isButtonDisabled.value) {
        if (item.campaignType === 'Ticket' && selectedVariants.value.length === 0) {
          ElMessage.warning('Please select a ticket variant');
          // Scroll to variants section
          const variantsSection = document.querySelector('.variants-picker');
          if (variantsSection) {
            variantsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else if (item.quickCosts?.length && !(customAmount.value >= 0)) {
          ElMessage.warning('Please enter a donation amount');
          // Focus on amount input
          const amountInput = document.getElementById('custom-amount-input');
          if (amountInput) {
            amountInput.focus();
            amountInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return;
      }

      // Proceed with normal action
      onStickerActionButtonClick();
    }

    function addTicketLinesToCart(navigateToCheckout = false) {
      const stickerId = String(item.id);
      const stickerTitle = item.title || '';
      const creatorId = item.organization?.id || item.user?.id || '';
      const creatorTitle = item.creatorTitle || item.organization?.name
        || `${item.user?.firstname || ''} ${item.user?.lastname || ''}`.trim() || '';

      if (navigateToCheckout) {
        store.dispatch('cart/clearCart');
      }

      // Pre-validate donation add-ons before touching the cart
      for (const [addOnId, selection] of Object.entries(selectedAddOns.value)) {
        if (!selection.selected) continue;
        const addOn = item.addOns?.find((a: any) => a.id === addOnId);
        if (!addOn || addOn.type !== 'Donation') continue;

        const validCauses: string[] = (addOn.donationCauses || []).filter(
          (c: string) => c && c.trim().length > 0,
        );
        if (validCauses.length === 0) {
          ElMessage.warning('This donation add-on has no charity configured. Please contact the event organizer.');
          return;
        }
        if (validCauses.length > 1) {
          const selectedCauseCount = selection.charities
            ? Object.keys(selection.charities).length
            : 0;
          if (selectedCauseCount === 0) {
            ElMessage.warning('Please select at least one charity to support before adding the donation.');
            return;
          }
        }
      }

      for (const variantId of selectedVariants.value) {
        const variant = item.varients?.find((v: any) => v.id === variantId);
        if (!variant) continue;
        store.dispatch('cart/addLine', {
          stickerId,
          stickerTitle,
          creatorId,
          creatorTitle,
          lineType: 'Ticket',
          variantId,
          variantTitle: variant.title,
          quantity: selectedVariantsAmounts.value[variantId] || 1,
          unitCost: variant.cost,
          isFree: variant.cost === 0 || variant.cost === '0',
          selectedOption: selectedVariantOptions.value[variantId] || undefined,
        });
      }

      for (const [addOnId, selection] of Object.entries(selectedAddOns.value)) {
        if (!selection.selected) continue;
        const addOn = item.addOns?.find((a: any) => a.id === addOnId);
        if (!addOn) continue;

        if (addOn.type === 'Donation') {
          const validCauses: string[] = (addOn.donationCauses || []).filter(
            (c: string) => c && c.trim().length > 0,
          );
          const donationAmount = selection.donationAmount
            ?? (addOn.donationPricing === 'fixed' ? addOn.donationFixed : undefined);
          const selectedCauseIds = selection.charities
            ? Object.keys(selection.charities)
            : [];

          // Build array of selected cause IDs
          const causeIds = selectedCauseIds.length > 0
            ? selectedCauseIds
            : [selection.donationCauseId?.trim() || validCauses[0]];

          // Add ONE cart line with donationCauseIds array
          if (causeIds.length > 1) {
            store.dispatch('cart/addLine', {
              stickerId,
              stickerTitle,
              creatorId,
              creatorTitle,
              lineType: 'Donation',
              addOnId,
              donationCauseIds: causeIds,
              donationAmount,
            });
          } else {
            // Single cause - use old field for backward compatibility
            store.dispatch('cart/addLine', {
              stickerId,
              stickerTitle,
              creatorId,
              creatorTitle,
              lineType: 'Donation',
              addOnId,
              donationCauseId: causeIds[0],
              donationAmount,
            });
          }
        } else {
          const sweepstakesPrice = addOn.type === 'Sweepstakes'
            ? calculateSweepstakesPrice(addOn, selection.sweepstakesEntries || 0)
            : 0;
          store.dispatch('cart/addLine', {
            stickerId,
            stickerTitle,
            creatorId,
            creatorTitle,
            lineType: addOn.type as 'Glow' | 'Sweepstakes',
            addOnId,
            unitCost: addOn.type === 'Glow' ? (addOn.glowPrice ?? 0) : sweepstakesPrice,
            isFree: addOn.type === 'Glow' ? (addOn.glowIsFree ?? true) : sweepstakesPrice === 0,
            sweepstakesEntries: selection.sweepstakesEntries,
          });
        }
      }

      if (navigateToCheckout) {
        navigateToCheckout(router, route, {
          query: { isWebUser: 'true' },
          returnLabel: item.title || undefined,
        });
        return;
      }

      flashAddedToCartBanner();
    }

    async function redirectToLegacyPurchase(
      variants?: string,
      quantities?: string,
    ) {
      let causeId = 'NA';

      if (item.causes.length === 1) {
        causeId = item.causes[0].split(' : ')[1] || item.causes[0];
      } else if (selectedCauseIds.value.length > 0) {
        [causeId] = selectedCauseIds.value;
      } else if (selectedCause.value) {
        const [, id] = selectedCause.value.split(' : ');
        causeId = id;
      }

      const stickerWebIntent = store.getters['users/me'] != null
        ? await doCreateStickerWebIntent(
          item.id,
          causeId,
          customAmount.value ?? 'NA',
          socialShopId.value,
          glowId.value,
        )
        : await doCreateStickerWebIntentPublic(
          item.id,
          causeId,
          customAmount.value ?? 'NA',
          socialShopId.value,
          glowId.value,
        );

      let url = stickerWebIntent;

      if (variants?.length && quantities?.length) {
        if (url.includes('?cost')) {
          const [baseUrl, costParam] = url.split('?');
          url = `${baseUrl}/variants/${variants}/quantities/${quantities}?${costParam}`;
        } else {
          url = `${url}/variants/${variants}/quantities/${quantities}`;
        }
      }

      const finalUrl = resolveEmbeddedPaymentUrl(addUserToUrl(addEmbeddedContextToUrl(url)));
      prepareEmbedPaymentNavigation();
      window.location.href = finalUrl;
    }

    interface CartAddOverrides {
      unitCost?: number;
      quantity?: number;
      donationAmount?: number;
      sweepstakesEntryCost?: number;
    }

    function addStickerToCart(overrides: CartAddOverrides = {}) {
      const stickerId = String(item.id);
      const stickerTitle = item.title || '';
      const creatorId = item.organization?.id || item.user?.id || '';
      const creatorTitle = item.creatorTitle || item.organization?.name
        || `${item.user?.firstname || ''} ${item.user?.lastname || ''}`.trim() || '';
      const campaignType = item.campaignType;

      // Build base line
      const line: any = {
        stickerId,
        stickerTitle,
        creatorId,
        creatorTitle,
        lineType: 'Sticker',
        unitCost: overrides.unitCost ?? item.cost ?? 0,
        quantity: overrides.quantity ?? 1,
        campaignType,
      };

      // Add campaign-type-specific fields
      if (campaignType === 'Campaign' || campaignType === 'MatchCampaign') {
        // Donation sticker - require cause selection
        const causeIds = selectedCauseIds.value.length > 0
          ? selectedCauseIds.value
          : item.causes?.length === 1
          ? [item.causes[0].split(' : ')[1] || item.causes[0]]
          : [];

        // Use custom amount if available (quickCosts), otherwise use sticker cost
        const donationAmount = overrides.donationAmount
          ?? customAmount.value
          ?? item.cost
          ?? 0;
        line.unitCost = donationAmount;
        line.donationAmount = donationAmount;

        if (causeIds.length > 1) {
          line.donationCauseIds = causeIds;
        } else if (causeIds.length === 1) {
          line.donationCauseId = causeIds[0];
        }
      }

      if (campaignType === 'Sweepstakes' && item.sweepstakesSticker) {
        // Sweepstakes - use custom amount as entry tier selection
        const entryCost = overrides.sweepstakesEntryCost ?? customAmount.value ?? 0;
        line.sweepstakesEntryCost = entryCost;
        line.unitCost = entryCost;
      }

      // Optional: variant, social shop, glow
      if (selectedVariants.value.length > 0 && overrides.quantity == null) {
        const variantId = selectedVariants.value[0];
        const variant = item.varients?.find((v: any) => v.id === variantId);
        if (variant) {
          line.variantId = variantId;
          line.variantTitle = variant.title;
          line.unitCost = variant.cost || line.unitCost;
          line.quantity = selectedVariantsAmounts.value[variantId] || 1;
        }
      }

      if (socialShopId.value) line.socialShopId = socialShopId.value;
      if (glowId.value) line.glowId = glowId.value;

      // Add to cart
      store.dispatch('cart/addLine', line);

      flashAddedToCartBanner();
    }

    function shouldShowCartAmountDialogBeforeAdd(): boolean {
      if (isEmbedded.value) return false;
      if (item.campaignType?.toLowerCase() === 'ticket') return false;
      if (!shouldPromptCartAmountBeforeAdd(item)) return false;
      if (stickerHasInlineCartAmount(item, customAmount.value)) return false;
      if (
        item.campaignType?.toLowerCase() === 'sweepstakes'
        && customAmount.value != null
        && !Number.isNaN(Number(customAmount.value))
      ) {
        return false;
      }
      return true;
    }

    function openCartAmountDialogBeforeAdd() {
      cartAmountDialogItems.value = [buildCartAmountDialogItem(item, 1)];
      showCartAmountDialog.value = true;
    }

    function handleCartAmountDialogConfirm(entries: CartAmountConfirmEntry[]) {
      const entry = entries[0];
      if (!entry) return;
      addStickerToCart({
        unitCost: entry.unitCost,
        quantity: entry.quantity,
        donationAmount: entry.donationAmount,
        sweepstakesEntryCost: entry.sweepstakesEntryCost,
      });
    }

    function scrollToAndHighlightAmount() {
      const el = amountInputWrapperRef.value
        || amountInputDesktopWrapperRef.value
        || document.getElementById('custom-amount-input')?.closest('.amount-input-wrapper');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el.querySelector('input') as HTMLInputElement | null)?.focus();
      }
      amountInputHighlight.value = true;
      setTimeout(() => { amountInputHighlight.value = false; }, 1500);
    }

    const isCampaignBadgeClickable = computed(() => {
      const campaignType = item.campaignType?.toLowerCase();
      return campaignType === 'campaign' || campaignType === 'matchcampaign';
    });

    function scrollToCampaignCta() {
      const el = document.querySelector('.campaign-cta-btn-fixed, .primary-action-button');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      campaignCtaHighlight.value = true;
      setTimeout(() => { campaignCtaHighlight.value = false; }, 1500);
    }

    function handleCampaignBadgeClick() {
      if (!isCampaignBadgeClickable.value) return;

      if (item.quickCosts?.length) {
        scrollToAndHighlightAmount();
        campaignCtaHighlight.value = true;
        setTimeout(() => { campaignCtaHighlight.value = false; }, 1500);
        return;
      }

      scrollToCampaignCta();
    }

    async function onStickerActionButtonClick() {
      // Campaign + quickCosts: scroll to inline amount input when visible, else use cart dialog
      if (
        item.quickCosts?.length
        && !(customAmount.value >= 0)
        && (item.campaignType === 'Campaign' || item.campaignType === 'campaign' || !item.campaignType)
      ) {
        const hasInlineAmountInput = amountInputWrapperRef.value
          || amountInputDesktopWrapperRef.value;
        if (hasInlineAmountInput) {
          scrollToAndHighlightAmount();
          return;
        }
      }

      const wasDialogOpen = isToChooseCause.value;
      isToChooseCause.value = false;

      const variants = selectedVariants.value?.join(',');
      const quantities = selectedVariants.value
        .map((variant) => selectedVariantsAmounts.value[variant])
        .filter((value) => value !== 0)
        .join(',');

      // Check for charity selection first for campaigns with multiple causes (but not sweepstakes)
      if (item.causes.length > 1 && selectedCauseIds.value.length === 0 && item.campaignType != null && item.campaignType.toLowerCase() !== 'sweepstakes') {
        isToChooseCause.value = true;
        return;
      }

      // Show loading indicator for all redirect scenarios
      loading.value = true;

      try {
        // Handle free stickers and free sweepstakes - redirect to legacy checkout page
        // Note: free ticket events are handled by addTicketLinesToCart below (free: true path)
        if ((item.cost === 0 && !item.varients.length
            && !item.sweepstakesSticker && !item.quickCosts?.length)
            || (item.sweepstakesSticker && customAmount.value === 0)) {
          const isEmbedded = route.path.includes('/embed/');
          // Preserve query parameters for embedded context
          const queryParams = new URLSearchParams();

          // Copy existing query parameters (including glowId)
          if (route.query) {
            Object.entries(route.query).forEach(([key, value]) => {
              if (value) queryParams.set(key, String(value));
            });
          }

          const queryString = queryParams.toString();
          const queryParam = queryString ? `?${queryString}` : '';
          const checkoutPath = isEmbedded
            ? `/web/embed/sticker/checkout/${item.id}${queryParam}`
            : `/web/sticker/checkout/${item.id}${queryParam}`;

          router.push(checkoutPath);
          return;
        }

        // Paid Ticket campaign → cart flow (direct checkout when embedded)
        if (item.campaignType?.toLowerCase() === 'ticket') {
          addTicketLinesToCart(isEmbedded.value);
          loading.value = false;
          return;
        }

        // Handle cause selection for campaigns
        if (item.causes.length === 1) {
          // Auto-select the only available cause
          selectedCauseIds.value = [item.causes[0].split(' : ')[1] || item.causes[0]];
        }

        if (isEmbedded.value) {
          await redirectToLegacyPurchase(variants, quantities);
          return;
        }

        // Standalone sticker (Campaign, MatchCampaign, Sweepstakes, plain paid) → use cart flow
        if (shouldShowCartAmountDialogBeforeAdd()) {
          openCartAmountDialogBeforeAdd();
          loading.value = false;
          return;
        }
        addStickerToCart();
        loading.value = false;
      } catch (err) {
        console.error('Error processing sticker action:', err);
        loading.value = false;
      }
    }

    function getformattedDate(timeOffset: string | number | Date) {
      const t = dayjs(timeOffset).format('MM/DD/YY');
      return t;
    }

    function getformattedTime(timeOffset: string | number | Date) {
      const t = dayjs(timeOffset).format('hh:mm a');
      return t;
    }

    function getFormattedEventDateTime(timeOffset: string | number | Date) {
      return dayjs(timeOffset).format('dddd MMMM Do • h:mm A');
    }

    function formatGlowDate(timeOffset: string | number | Date) {
      return dayjs(timeOffset).format('MMM D, YYYY');
    }

    function formatGlowTime(timeOffset: string | number | Date) {
      const timezone = item.glow?.glowStartDateTimeZone;
      const timeStr = dayjs(timeOffset).format('h:mm A');
      return timezone ? `${timeStr} ${timezone}` : timeStr;
    }

    function getNonProfitName(beneficiary: string) {
      // Extract the name part before the colon if it exists
      if (beneficiary && beneficiary.includes(' : ')) {
        return beneficiary.split(' : ')[0];
      }
      return beneficiary;
    }

    const validateCustomAmount = (event: Event) => {
      const target = event.target as HTMLInputElement;
      let value = Number(target.value);
      if (value < 1) {
        value = 1;
      }
      customAmount.value = value;
      target.value = value.toString();
    };

    const handleStickerImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      if (!target) return;
      // Hide the image and show parent container background
      target.style.display = 'none';
    };

    const handleAvatarImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      if (!target) return;
      // Use default profile image for avatar errors
      const defaultProfileImage = '/src/assets/images/default-profile.jpeg';
      if (target.src !== defaultProfileImage) {
        target.src = defaultProfileImage;
      }
    };

    // Fetch user's shop stickers
    async function fetchUserShopStickers() {
      const me = store.getters['users/me'];
      if (!me || !me.id) return;

      try {
        const response = await makeRequest(
          'query',
          findPublishedStickersShortDetailsWithSocialShop,
          {
            filter: ['Active'],
            take: 1000,
            skip: 0,
            socialShopUserId: me.id,
          },
          { token: store.getters['users/getToken'] },
        );

        if (response?.data?.findPublishedStickersShortDetails) {
          const stickers = response.data.findPublishedStickersShortDetails;
          const shopIds = new Set<string>();
          stickers.forEach((sticker: any) => {
            if (sticker.socialShop && sticker.socialShop.id) {
              shopIds.add(sticker.id);
            }
          });
          userShopStickerIds.value = shopIds;
        }
      } catch (err) {
        console.error('Failed to fetch user shop stickers:', err);
      }
    }

    async function fetchUserFavorites() {
      const me = store.getters['users/me'];
      if (!me || !me.id) return;

      try {
        const favorites = await getMyFavorites();
        const favoriteIds = new Set<string>();
        if (Array.isArray(favorites)) {
          favorites.forEach((sticker: any) => {
            if (sticker.id) {
              favoriteIds.add(sticker.id.toString());
            }
          });
        }
        userFavoritedStickerIds.value = favoriteIds;
      } catch (err) {
        console.error('Failed to fetch user favorites:', err);
      }
    }

    async function fetchAnnouncements() {
      if (!item.id || item.campaignType !== 'Ticket') return;

      announcementsLoading.value = true;
      try {
        const response = await makeRequest(
          'query',
          findStickerAnnouncements,
          { stickerId: item.id.toString() },
          null,
        );
        if (response?.data?.findStickerAnnouncements) {
          stickerAnnouncements.value = response.data.findStickerAnnouncements;
        }
      } catch (err) {
        console.error('Failed to fetch announcements:', err);
      } finally {
        announcementsLoading.value = false;
      }
    }

    function formatAnnouncementDate(date: string) {
      return dayjs(date).format('MMM D, YYYY h:mm A');
    }

    async function handleSendQuestion() {
      if (!questionFormRef.value) return;

      try {
        await questionFormRef.value.validate();
      } catch {
        return;
      }

      sendingQuestion.value = true;
      try {
        const response = await makeRequest(
          'mutation',
          askStickerHost,
          {
            stickerId: item.id.toString(),
            question: questionForm.value.question,
            email: questionForm.value.email || undefined,
            name: questionForm.value.name || undefined,
          },
          { token: store.getters['users/getToken'] },
        );

        if (response?.errors) throw new Error(response.errors[0]?.message);

        showWebAppSuccessBanner('Your question has been sent to the host');
        showQuestionDialog.value = false;
        resetQuestionForm();
      } catch (err: any) {
        console.error('Failed to send question:', err);
        ElMessage.error(err.message || 'Failed to send question');
      } finally {
        sendingQuestion.value = false;
      }
    }

    function resetQuestionForm() {
      questionForm.value.question = '';
      questionForm.value.name = '';
      questionForm.value.email = '';
      if (questionFormRef.value) {
        questionFormRef.value.clearValidate();
      }
    }

    // Add sticker to shop
    async function addStickerToShop() {
      if (!item.id) return;

      shopActionLoading.value = true;
      try {
        const response = await makeRequest(
          'mutation',
          mutateSocialShopSticker,
          { stickerId: item.id.toString() },
          { token: store.getters['users/getToken'] },
        );

        if (response?.data?.mutateSocialShopSticker) {
          userShopStickerIds.value.add(item.id.toString());
          showWebAppSuccessBanner('Added to your shop');
        } else if (response?.errors) {
          throw new Error(response.errors[0]?.message || 'Failed to add to shop');
        }
      } catch (err: any) {
        console.error('Failed to add sticker to shop:', err);
        ElMessage.error(err.message || 'Failed to add to shop');
      } finally {
        shopActionLoading.value = false;
      }
    }

    // Remove sticker from shop
    async function removeStickerFromShop() {
      if (!item.id) return;

      shopActionLoading.value = true;
      try {
        const response = await makeRequest(
          'mutation',
          mutateRemoveSocialShopSticker,
          { stickerId: item.id.toString() },
          { token: store.getters['users/getToken'] },
        );

        if (response?.data?.mutateRemoveSocialShopSticker) {
          userShopStickerIds.value.delete(item.id.toString());
          showWebAppSuccessBanner('Removed from your shop');
        } else if (response?.errors) {
          throw new Error(response.errors[0]?.message || 'Failed to remove from shop');
        }
      } catch (err: any) {
        console.error('Failed to remove sticker from shop:', err);
        ElMessage.error(err.message || 'Failed to remove from shop');
      } finally {
        shopActionLoading.value = false;
      }
    }

    // Handle add to shop button click
    async function handleAddToShop() {
      const me = store.getters['users/me'];
      if (!me) {
        // Redirect to login page
        router.push(`/auth/web/signin?stickerId=${item.id}`);
        return;
      }

      if (isInShop.value) {
        await removeStickerFromShop();
      } else {
        await addStickerToShop();
      }
    }

    async function handleToggleFavorite() {
      const me = store.getters['users/me'];
      if (!me) {
        router.push(`/auth/web/signin?stickerId=${item.id}`);
        return;
      }

      if (!item.id) return;

      favoriteLoading.value = true;
      try {
        const newState = await toggleFavorite(item.id.toString());
        if (newState) {
          userFavoritedStickerIds.value.add(item.id.toString());
          showWebAppSuccessBanner('Saved to favorites');
        } else {
          userFavoritedStickerIds.value.delete(item.id.toString());
          showWebAppSuccessBanner('Removed from favorites');
        }
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
        ElMessage.error('Failed to update favorites');
      } finally {
        favoriteLoading.value = false;
      }
    }

    function navigateToStickerAnalytics() {
      if (!item.id) return;
      const slugOrId = item.slug || item.id;
      const basePath = isEmbedded.value
        ? `/web/embed/sticker/${slugOrId}/analytics`
        : `/web/sticker/${slugOrId}/analytics`;
      router.push(basePath);
    }

    function formatCompactCount(count: number | undefined | null): string {
      return formatCountNoDecimal(Number(count) || 0);
    }

    async function trackStickerPageView() {
      if (!item.id) return;

      try {
        await makeRequest(
          'mutation',
          trackStickerView,
          { stickerId: item.id.toString() },
          {},
        );
      } catch (err) {
        console.error('Failed to track sticker view:', err);
      }
    }

    async function trackStickerShareComplete() {
      if (!item.id) return;

      try {
        await makeRequest(
          'mutation',
          trackStickerShare,
          { stickerId: item.id.toString() },
          {},
        );
      } catch (err) {
        console.error('Failed to track sticker share:', err);
      }
    }

    function handleEmbedBack() {
      if (isEmbeddedContext()) {
        const navigated = navigateToEmbedOrganizationHome(route, undefined, { router });
        if (navigated) {
          return;
        }
      }

      const homeUrl = getEmbedOrganizationHomeUrl();
      if (homeUrl) {
        router.push(homeUrl);
        return;
      }

      router.back();
    }

    // Handle share button click
    async function handleShare() {
      showShareModal.value = true;
    }

    // Toggle FAQ accordion
    function toggleFaq(faqId: string) {
      if (expandedFaqId.value === faqId) {
        expandedFaqId.value = null;
      } else {
        expandedFaqId.value = faqId;
      }
    }

    // Get shareable URL for the sticker
    function getStickerUrl() {
      return `${import.meta.env.VITE_WEB_BASE_URL}/web/sticker/${item.slug || item.id}`;
    }

    // Copy URL to clipboard
    async function copyUrlToClipboard() {
      const url = getStickerUrl();

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          showWebAppSuccessBanner('Link copied to clipboard');
          await trackStickerShareComplete();
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = url;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          showWebAppSuccessBanner('Link copied to clipboard');
          await trackStickerShareComplete();
        }
      } catch (err) {
        console.error('Failed to copy URL:', err);
        ElMessage.error('Failed to copy link');
      }
    }

    // Share via native share API
    async function shareViaSystem() {
      const url = getStickerUrl();

      if (navigator.share) {
        try {
          await navigator.share({
            title: item.title || 'Check out this sticker',
            text: item.description || '',
            url,
          });
          await trackStickerShareComplete();
        } catch (err: any) {
          // User cancelled or share failed
          if (err.name !== 'AbortError') {
            console.error('Share failed:', err);
            // Fallback to copy
            await copyUrlToClipboard();
          }
        }
      } else {
        // Fallback to copy if Web Share API not available
        await copyUrlToClipboard();
      }
    }

    // Close share modal
    function closeShareModal() {
      showShareModal.value = false;
    }

    function openMyTicketsDialog() {
      if (item.id) {
        router.push(`/web/ticket/${item.id}/detail`);
      }
    }

    function updateAddonDonationAmount(addonId: string, rawValue: string) {
      customDonationInputs.value[addonId] = rawValue;
      const amount = Number(rawValue);
      selectedAddOns.value[addonId] = {
        ...selectedAddOns.value[addonId],
        selected: true,
        donationAmount: amount,
      };
    }

    function updateAddonSelected(addonId: string, checked: boolean) {
      selectedAddOns.value = {
        ...selectedAddOns.value,
        [addonId]: {
          ...selectedAddOns.value[addonId],
          selected: checked,
        },
      };
    }

    function handleGlowAddonRegister(addon: any) {
      console.log('[GlowAddon] Register clicked', {
        addonId: addon.id,
        glowId: addon.glowId,
        glowName: addon.glowName,
        glowIsFree: addon.glowIsFree,
        glowPrice: addon.glowPrice,
        glowIsExisting: addon.glowIsExisting,
      });

      if (addon.glowId) {
        updateAddonSelected(addon.id, true);
        console.log('[GlowAddon] Opening glow page in new tab');
        window.open(`/web/glow/${addon.glowId}`, '_blank');
      } else {
        console.warn('[GlowAddon] No glowId on addon — marking selected locally only (no glow linked yet)');
        updateAddonSelected(addon.id, true);
      }
    }

    function setAddonSuggestedAmount(addonId: string, amount: number) {
      customDonationInputs.value[addonId] = '';
      selectedAddOns.value[addonId] = {
        ...selectedAddOns.value[addonId],
        selected: true,
        donationAmount: amount,
      };
    }

    // Sweepstakes add-on functions
    function updateSweepstakesEntries(addonId: string, rawValue: string) {
      const entries = Number(rawValue);
      selectedAddOns.value[addonId] = {
        ...selectedAddOns.value[addonId],
        selected: entries > 0,
        sweepstakesEntries: entries,
      };
    }

    function getLinkedSweepstakes(addon: any): any {
      return addon?.sweepstakesStickerId
        ? sweepstakesLinkedStickers.value[addon.sweepstakesStickerId]
        : null;
    }

    function getSweepstakesEntryPrice(addon: any): string {
      const linked = getLinkedSweepstakes(addon);
      const costs: number[] = linked?.sweepstakesSticker?.entryCost || [];
      // First entry is free; use the first non-zero cost tier as the per-entry price
      const paidCost = costs.find((c: number) => c > 0);
      return paidCost != null ? paidCost.toFixed(2) : '5.00';
    }

    function calculateSweepstakesPrice(addon: any, entries: number): number {
      if (entries === 0) return 0;
      if (entries === 1) return 0; // First entry is free
      const linked = getLinkedSweepstakes(addon);
      const costs: number[] = linked?.sweepstakesSticker?.entryCost || [];
      if (costs.length > 0) {
        // Sum all entryCost values after the first (free) entry
        return costs.slice(1, entries).reduce((sum: number, c: number) => sum + (c || 0), 0);
      }
      const entryPrice = parseFloat(getSweepstakesEntryPrice(addon));
      return (entries - 1) * entryPrice;
    }

    function getEntriesBreakdown(entries: number): string {
      if (entries === 0) return '';
      if (entries === 1) return '1 free';
      const freeCount = 1;
      const paidCount = entries - 1;
      return `${freeCount} free + ${paidCount} paid`;
    }

    // Multi-charity donation functions
    function getDonationPricingMode(addon: {
      donationPricing?: string | null;
      donationFixed?: number | null;
    }): 'fixed' | 'suggested' | 'custom' {
      const pricing = (addon.donationPricing || 'custom').toLowerCase();
      if (pricing === 'fixed' && addon.donationFixed != null) {
        return 'fixed';
      }
      if (pricing === 'suggested') {
        return 'suggested';
      }
      return 'custom';
    }

    function isCharitySelected(addonId: string, charityId: string): boolean {
      const charities = selectedAddOns.value[addonId]?.charities;
      return charities ? charityId in charities : false;
    }

    function getSelectedCharitiesCount(addonId: string): number {
      const charities = selectedAddOns.value[addonId]?.charities || {};
      return Object.keys(charities).length;
    }

    function toggleCharity(addonId: string, charityId: string) {
      if (!selectedAddOns.value[addonId]) {
        selectedAddOns.value[addonId] = { selected: false };
      }
      if (!selectedAddOns.value[addonId].charities) {
        selectedAddOns.value[addonId].charities = {};
      }

      const charities = selectedAddOns.value[addonId].charities!;
      if (charityId in charities) {
        delete charities[charityId];
      } else {
        charities[charityId] = 1;
      }
    }

    // Handle glow badge click
    function handleGlowClick() {
      if (item.glow?.id) {
        const glowIdentifier = item.glow.slug || item.glow.id;
        window.open(`/web/glow/${glowIdentifier}`, '_blank');
      }
    }

    // Format ticket price range from API string (e.g., "$25-$75" or "$50")
    function formatTicketPriceRangeFromString(priceRange: string): string {
      if (!priceRange) return 'Free';
      
      // Try to parse ranges like "$25-$75"
      const rangeMatch = priceRange.match(/\$(\d+(?:\.\d+)?)-\$(\d+(?:\.\d+)?)/);
      if (rangeMatch) {
        const min = parseFloat(rangeMatch[1]);
        const max = parseFloat(rangeMatch[2]);
        return formatPriceRangeAmountWithFees(min, max);
      }
      
      // Try to parse single price like "$50"
      const singleMatch = priceRange.match(/\$(\d+(?:\.\d+)?)/);
      if (singleMatch) {
        const price = parseFloat(singleMatch[1]);
        return formatPriceAmountWithFees(price);
      }
      
      // If can't parse, return as-is (backend may have already formatted it)
      return priceRange;
    }

    async function trackSocialShopView() {
      if (!socialShopId.value) return;

      try {
        await makeRequest(
          'mutation',
          trackSocialShopStickerView,
          { socialShopId: socialShopId.value },
          {},
        );
      } catch (err) {
        console.error('Failed to track social shop view:', err);
      }
    }

    onMounted(async () => {
      loading.value = true;
      await getItem();

      const me = store.getters['users/me'];
      if (me) {
        await loadHostOrganizationIds();
      }
      finalizeHostCheck();

      // Only check if sticker is purchased if there's no error and sticker was found
      if (!error.value && item.id) {
        await trackStickerPageView();
        if (socialShopId.value) {
          trackSocialShopView();
        }
        await doCheckIsStickerPurchased();
        if (item.campaignType?.toLowerCase() === 'ticket') {
          fetchAvailability(String(item.id));
        }
      }

      if (me) {
        await fetchUserShopStickers();
        await fetchUserFavorites();
      }

      // Fetch announcements for ticket-type stickers
      if (item.campaignType === 'Ticket') {
        await fetchAnnouncements();
      }

      store.commit('shared/SET_MAIN_MENU_COLLAPSE', false);
      loading.value = false;
      await nextTick();

      if (isEmbedded.value) {
        captureEmbedOrganizationContext(route);
        await nextTick();
        requestEmbedHeightSync();

        if (route.query.donationSuccess === 'true') {
          ElMessage({
            message: 'Thank you for your donation!',
            type: 'success',
            plain: true,
          });
        }
      }

      // Initial scroll on mobile devices (non-embed only)
      if (window.innerWidth <= 768 && !isEmbedded.value) {
        setTimeout(() => {
          window.scrollTo({
            top: 500,
            behavior: 'smooth',
          });
        }, 100);
      }
    });

    watch(loading, async (isLoading) => {
      if (!isLoading && isEmbedded.value) {
        await nextTick();
        requestEmbedHeightSync();
      }
    });

    const variantsRange = computed(() => {
      const range = item.varients.map((i) => Number(i.cost));
      range.sort((a, b) => a - b);
      const r1 = range[0] ?? 0;
      const r2 = range[range.length - 1] ?? 0;
      if (r1 === r2) return r1 === 0 ? 'Free' : formatPriceAmountWithFees(r1);
      return formatPriceRangeAmountWithFees(r1, r2);
    });

    const sweepstakesRange = computed(() => {
      if (Array.isArray(item.sweepstakesSticker.entryCost)
        && item.sweepstakesSticker.entryCost.length > 0) {
        const range = [...item.sweepstakesSticker.entryCost, 0].sort((a:any, b:any) => a - b);
        const r1 = range[0];
        const r2 = range[range.length - 1] ?? 0;
        return formatPriceRangeAmountWithFees(r1, r2);
      }
      return '';
    });

    const quickCostsRange = computed(() => {
      if (Array.isArray(item.quickCosts) && item.quickCosts.length > 0) {
        const sortedCosts = [...item.quickCosts].sort((a:any, b:any) => a - b);
        const min = sortedCosts[0];
        const max = sortedCosts[sortedCosts.length - 1];
        return formatPriceRangeAmountWithFees(min, max);
      }
      return '';
    });

    const totalSelectedTickets = computed(() => {
      const ids = selectedVariants.value;
      return ids.reduce((sum, id) => sum + (selectedVariantsAmounts.value[id] || 0), 0);
    });

    function getTicketActionLabel() {
      const label = getButtonLabel();
      if (totalSelectedTickets.value > 0) {
        return `${label} (${totalSelectedTickets.value})`;
      }
      return label;
    }

    const cartItemCount = computed(() => store.getters['cart/lineCount'] || 0);
    const maxCartQuantity = 99;
    const stickerCartPriceDrafts = ref<Record<string, string>>({});
    const inCartPanelEntering = ref(false);

    function flashAddedToCartBanner() {
      showWebAppSuccessBanner('Added to cart');
      inCartPanelEntering.value = false;
      nextTick(() => {
        inCartPanelEntering.value = true;
      });
    }

    const stickerCartLines = computed<CartLine[]>(() => {
      if (!item.id) return [];
      const stickerId = String(item.id);
      return (store.getters['cart/cartLines'] as CartLine[]).filter(
        (line) => line.stickerId === stickerId,
      );
    });

    const hasStickerInCart = computed(
      () => !isEmbedded.value && stickerCartLines.value.length > 0,
    );

    const primaryStickerCartLine = computed<CartLine | null>(() => (
      stickerCartLines.value.length === 1 ? stickerCartLines.value[0] : null
    ));

    function isDonationStyleStickerLine(line: CartLine): boolean {
      if (line.lineType !== 'Sticker') return false;
      const campaignType = String(line.campaignType || '').toLowerCase();
      if (campaignType === 'campaign' || campaignType === 'matchcampaign') return true;
      if (campaignType === 'sweepstakes' || line.sweepstakesEntryCost != null) return true;
      if (line.donationAmount != null) return true;
      if (line.donationCauseId || line.donationCauseIds?.length) return true;
      return false;
    }

    function getStickerCartLineUnitPrice(line: CartLine): number {
      if (line.lineType === 'Donation') return line.donationAmount ?? 0;
      if (line.sweepstakesEntryCost != null) return line.sweepstakesEntryCost;
      return line.unitCost ?? 0;
    }

    function calculateStickerCartLineTotal(line: CartLine): number {
      if (line.isFree) return 0;
      if (line.lineType === 'Ticket') {
        return (line.unitCost ?? 0) * (line.quantity ?? 1);
      }
      if (line.lineType === 'Donation') {
        const amount = line.donationAmount ?? 0;
        const causeCount = line.donationCauseIds?.length ?? 1;
        return amount * causeCount;
      }
      if (line.lineType === 'Sticker') {
        const amount = (line.unitCost ?? 0) * (line.quantity ?? 1);
        const causeCount = line.donationCauseIds?.length ?? 1;
        return amount * causeCount;
      }
      return line.unitCost ?? 0;
    }

    const stickerCartItemCount = computed(() => stickerCartLines.value.reduce(
      (sum, line) => sum + (line.quantity ?? 1),
      0,
    ));

    const stickerCartSubtotal = computed(() => stickerCartLines.value
      .reduce((sum, line) => sum + calculateStickerCartLineTotal(line), 0)
      .toFixed(2));

    const canAdjustStickerCartQuantity = computed(() => {
      const line = primaryStickerCartLine.value;
      if (!line) return false;
      if (line.lineType === 'Ticket') return true;
      if (line.lineType === 'Sticker' && !isDonationStyleStickerLine(line)) return true;
      return false;
    });

    const canEditStickerCartPrice = computed(() => {
      const line = primaryStickerCartLine.value;
      if (!line || line.isFree) return false;
      if (line.lineType === 'Ticket') return false;
      return true;
    });

    function getStickerCartLineQuantity(line: CartLine): number {
      return line.quantity ?? 1;
    }

    function getStickerCartPriceDisplay(line: CartLine): string {
      const draft = stickerCartPriceDrafts.value[line.id];
      if (draft !== undefined) return draft;
      return getStickerCartLineUnitPrice(line).toFixed(2);
    }

    function onStickerCartPriceInput(lineId: string, event: Event) {
      const target = event.target as HTMLInputElement | null;
      if (!target) return;
      stickerCartPriceDrafts.value = {
        ...stickerCartPriceDrafts.value,
        [lineId]: target.value,
      };
    }

    function applyStickerCartLineUnitPrice(line: CartLine, price: number) {
      const updated: CartLine = { ...line };
      if (line.lineType === 'Donation') {
        updated.donationAmount = price;
      } else {
        updated.unitCost = price;
        if (line.sweepstakesEntryCost != null) {
          updated.sweepstakesEntryCost = price;
        }
        const campaignType = String(line.campaignType || '').toLowerCase();
        if (
          campaignType === 'campaign'
          || campaignType === 'matchcampaign'
          || line.donationCauseId
          || line.donationCauseIds?.length
          || line.donationAmount != null
        ) {
          updated.donationAmount = price;
        }
      }
      store.dispatch('cart/updateLine', updated);
    }

    function commitStickerCartPrice(line: CartLine) {
      const draft = stickerCartPriceDrafts.value[line.id];
      const raw = draft !== undefined ? draft : getStickerCartLineUnitPrice(line).toFixed(2);
      const cleaned = raw.replace(/[^0-9.]/g, '');
      const parsed = parseFloat(cleaned);

      const nextDrafts = { ...stickerCartPriceDrafts.value };
      delete nextDrafts[line.id];
      stickerCartPriceDrafts.value = nextDrafts;

      if (Number.isNaN(parsed) || parsed < 0) return;

      const rounded = Math.round(parsed * 100) / 100;
      if (rounded === getStickerCartLineUnitPrice(line)) return;
      applyStickerCartLineUnitPrice(line, rounded);
    }

    function incrementStickerCartQuantity() {
      const line = primaryStickerCartLine.value;
      if (!line) return;
      const current = getStickerCartLineQuantity(line);
      if (current >= maxCartQuantity) return;
      store.dispatch('cart/updateLine', { ...line, quantity: current + 1 });
    }

    async function decrementStickerCartQuantity() {
      const line = primaryStickerCartLine.value;
      if (!line) return;
      const current = getStickerCartLineQuantity(line);
      if (current <= 1) {
        const label = line.variantTitle || line.stickerTitle || 'this item';
        const confirmed = await confirmRemoveCartItem(label);
        if (!confirmed) return;
        store.dispatch('cart/removeLine', line.id);
        return;
      }
      store.dispatch('cart/updateLine', { ...line, quantity: current - 1 });
    }

    function goToCart() {
      navigateToCheckout(router, route, {
        returnLabel: item.title || undefined,
      });
    }

    function goToCheckout() {
      navigateToCheckout(router, route, {
        returnLabel: item.title || undefined,
      });
    }

    function continueShopping() {
      if (creatorProfilePath.value) {
        router.push(creatorProfilePath.value);
        return;
      }
      if (item.campaignType === 'Ticket') {
        router.push('/web/dashboard?filters=Ticket');
        return;
      }
      router.push('/web/dashboard');
    }

    const showStickerPrimaryCta = computed(() => {
      if (isEmbedded.value) return true;
      if (!hasStickerInCart.value) return true;
      if (item.campaignType === 'Ticket' && totalSelectedTickets.value > 0) return true;
      return false;
    });

    const showMobileFloatingAction = computed(() => {
      if (item.campaignType === 'Ticket') {
        if (showStickerPrimaryCta.value && totalSelectedTickets.value > 0) return true;
        if (hasStickerInCart.value) return true;
        return false;
      }
      return showStickerPrimaryCta.value || hasStickerInCart.value;
    });

    function goBack() {
      const hasSameOriginReferrer = typeof document !== 'undefined'
        && document.referrer
        && document.referrer.startsWith(window.location.origin);

      if (hasSameOriginReferrer) {
        router.back();
        return;
      }

      if (creatorProfilePath.value) {
        router.push(creatorProfilePath.value);
        return;
      }

      if (item.campaignType === 'Ticket') {
        router.push('/web/dashboard?filters=Ticket');
        return;
      }

      router.push('/web/dashboard');
    }

    const totalPrice = computed(() => {
      const ids = selectedVariants.value;
      let total = 0;

      // Add variant costs
      ids.forEach((id) => {
        const variant = item.varients?.find((v) => v.id === id);
        const quantity = selectedVariantsAmounts.value[id] || 0;
        if (variant && quantity > 0) {
          const variantCost = variant.isFree ? 0 : (Number(variant.cost) || 0);
          total += variantCost * quantity;

          // Add option group prices for this variant
          const optionPrice = getOptionsTotalPrice(id);
          total += optionPrice * quantity;
        }
      });

      // Add selected add-ons
      Object.entries(selectedAddOns.value).forEach(([addOnId, selection]) => {
        if (selection.selected) {
          const addOn = item.addOns?.find((a: any) => a.id === addOnId);
          if (addOn) {
            if (addOn.type === 'Donation') {
              const donationAmt = selection.donationAmount
                ?? (addOn.donationPricing === 'fixed' ? addOn.donationFixed : undefined);
              const amt = Number(donationAmt) || 0;
              const validCauseCount = (addOn.donationCauses || []).filter(
                (c: string) => c && c.trim().length > 0,
              ).length;
              if (validCauseCount > 1) {
                const selectedCount = getSelectedCharitiesCount(addOnId);
                if (selectedCount > 0) {
                  total += amt * selectedCount;
                }
              } else {
                total += amt;
              }
            } else if (addOn.type === 'Glow' && !addOn.glowIsFree && addOn.glowPrice) {
              total += Number(addOn.glowPrice) || 0;
            } else if (addOn.type === 'Sweepstakes' && selection.sweepstakesEntries) {
              total += calculateSweepstakesPrice(addOn, selection.sweepstakesEntries);
            }
          }
        }
      });

      return total;
    });

    const mainVariants = computed(() => (item.varients || []).filter((v) => !v.isAddOn && v.isActive !== false));

    const addonVariants = computed(() => (item.varients || []).filter((v) => v.isAddOn && v.isActive !== false));

    const sortedGallery = computed(() => {
      const gallery = item.gallery || [];
      return [...gallery].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    });

    const lightboxItem = ref<{ type: string; url: string; thumbnail?: string } | null>(null);
    const lightboxZoom = ref(1);
    const ZOOM_STEP = 0.25;
    const MIN_ZOOM = 0.5;
    const MAX_ZOOM = 4;

    const openLightbox = (media: { type: string; url: string; thumbnail?: string }) => {
      lightboxItem.value = media;
      lightboxZoom.value = 1;
    };
    const closeLightbox = () => { lightboxItem.value = null; lightboxZoom.value = 1; };
    const zoomIn = () => {
      lightboxZoom.value = Math.min(MAX_ZOOM, +(lightboxZoom.value + ZOOM_STEP).toFixed(2));
    };
    const zoomOut = () => {
      lightboxZoom.value = Math.max(MIN_ZOOM, +(lightboxZoom.value - ZOOM_STEP).toFixed(2));
    };
    const onLightboxBackdrop = (e: MouseEvent) => {
      if ((e.target as HTMLElement).classList.contains('c-lightbox')) closeLightbox();
    };

    const sortedFaqs = computed(() => {
      const faqs = item.faqs || [];
      return [...faqs].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    });

    const tabs = computed(() => {
      const base = ['Details', 'About Creator', 'Updates'];
      if (item.campaignType === 'Ticket') {
        base.push('Announcements');
      }
      return base;
    });

    // Check if sticker is in user's shop
    const isInShop = computed(() => (item.id ? userShopStickerIds.value.has(item.id.toString()) : false));

    const isFavorited = computed(() => (item.id ? userFavoritedStickerIds.value.has(item.id.toString()) : false));

    const isSignedIn = computed(() => {
      const me = store.getters['users/me'];
      return me !== null && me !== undefined;
    });

    const isEventHost = computed(() => {
      hostContextTick.value;
      managedOrganizationIds.value;
      if (!item.id) return false;
      return isHostOfSticker({
        entity: item.entity,
        entityId: item.entityId,
        organization: item.organization,
        user: item.user,
      });
    });

    const hasViewCount = computed(() => item.viewCount != null);

    const showInsightsBar = computed(
      () => hasViewCount.value || isEventHost.value,
    );

    const showInsightsButton = computed(
      () => hostCheckReady.value && isEventHost.value,
    );

    return {
      route,
      router,
      loading,
      store,
      // data
      item,
      getCausesList,
      causeLoading,
      causeOptions,
      causeOptionsArray,
      getCausesDropdownOptions,
      customLabel,
      selectedCause,
      firstCauseName,
      onStickerActionButtonClick,
      handleCollectBadgeClick,
      isPromptDownloadApp,
      isStickerPurchased,
      isToChooseCause,
      showCartAmountDialog,
      cartAmountDialogItems,
      handleCartAmountDialogConfirm,
      selectedCauseIds,
      getformattedDate,
      getformattedTime,
      formatGlowDate,
      formatGlowTime,
      formatDateTimeWithTimezone,
      formatDate,
      formatTime,
      getNonProfitName,
      addToCalendarClick,
      handleCauseChange,
      searchSelected,
      onSelectVariant,
      handleDropdownClick,
      handleDropdownChange,
      selectedVariants,
      selectedVariantsAmounts,
      customAmount,
      validateCustomAmount,
      maxValue,
      selectValue,
      currentEntryText,
      currentCostText,
      value,
      marks,
      variantsRange,
      sweepstakesRange,
      quickCostsRange,
      totalPrice,
      showLoginDialog,
      showSweepstakesAlreadyEnteredDialog,
      showSweepstakesRules,
      goToSignUp,
      goToSignIn,
      error,
      getButtonLabel,
      getTicketActionLabel,
      isEmbedded,
      isStandaloneEmbed,
      socialShopId,
      handleStickerImageError,
      handleAvatarImageError,
      activeTab,
      tabs,
      creatorName,
      creatorInitial,
      creatorProfilePath,
      handleCreatorClick,
      normalizedVirtualLink,
      virtualLinkDomain,
      getCauseName,
      getCauseEIN,
      isButtonDisabled,
      amountInputWrapperRef,
      amountInputHighlight,
      campaignCtaHighlight,
      isCampaignBadgeClickable,
      handleCampaignBadgeClick,
      scrollToAndHighlightAmount,
      // Shop functionality
      shopActionLoading,
      isInShop,
      favoriteLoading,
      isFavorited,
      announcementsLoading,
      stickerAnnouncements,
      formatAnnouncementDate,
      showQuestionDialog,
      sendingQuestion,
      questionFormRef,
      questionForm,
      questionRules,
      handleSendQuestion,
      resetQuestionForm,
      isSignedIn,
      isEventHost,
      hostCheckReady,
      hasViewCount,
      showInsightsBar,
      showInsightsButton,
      navigateToStickerAnalytics,
      formatCompactCount,
      handleAddToShop,
      handleToggleFavorite,
      handleShare,
      handleEmbedBack,
      showShareModal,
      closeShareModal,
      copyUrlToClipboard,
      shareViaSystem,
      getStickerUrl,
      openMyTicketsDialog,
      handleGlowClick,
      selectedVariantOptions,
      selectedAddOns,
      customDonationInputs,
      updateAddonDonationAmount,
      updateAddonSelected,
      handleGlowAddonRegister,
      setAddonSuggestedAmount,
      sweepstakesLinkedStickers,
      sweepstakesLinkedLoading,
      getLinkedSweepstakes,
      updateSweepstakesEntries,
      getSweepstakesEntryPrice,
      calculateSweepstakesPrice,
      getEntriesBreakdown,
      getDonationPricingMode,
      isCharitySelected,
      toggleCharity,
      getSelectedCharitiesCount,
      incrementVariant,
      decrementVariant,
      handleOptionSelect,
      isOptionSelected,
      getOptionsTotalPrice,
      selectedOptionGroups,
      totalSelectedTickets,
      cartItemCount,
      inCartPanelEntering,
      hasStickerInCart,
      showStickerPrimaryCta,
      primaryStickerCartLine,
      stickerCartItemCount,
      stickerCartSubtotal,
      canAdjustStickerCartQuantity,
      canEditStickerCartPrice,
      maxCartQuantity,
      getStickerCartLineQuantity,
      getStickerCartPriceDisplay,
      onStickerCartPriceInput,
      commitStickerCartPrice,
      incrementStickerCartQuantity,
      decrementStickerCartQuantity,
      goToCart,
      goToCheckout,
      continueShopping,
      showMobileFloatingAction,
      goBack,
      getFormattedEventDateTime,
      mainVariants,
      addonVariants,
      sortedGallery,
      sortedFaqs,
      expandedFaqId,
      toggleFaq,
      availabilityMap,
      lightboxItem,
      lightboxZoom,
      openLightbox,
      closeLightbox,
      zoomIn,
      zoomOut,
      onLightboxBackdrop,
      formatPriceWithFees,
      formatPriceRangeWithFees,
      formatAddonPriceWithFees,
      formatPriceAmountWithFees,
      formatPriceRangeAmountWithFees,
      formatAddonPriceAmountWithFees,
      formatTicketPriceRangeFromString,
    };
  },
});

</script>

<style lang="scss" scoped>
.sticker-analytics-bar {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0.375rem 0 0.75rem;

  .analytics-eye-icon {
    width: 0.875rem;
    height: 0.875rem;
    color: #717182;
    flex-shrink: 0;
  }

  .analytics-count {
    font-size: 12px;
    color: #717182;
  }

  .analytics-separator {
    font-size: 12px;
    color: #717182;
  }

  .analytics-link {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #0d9488;
    cursor: pointer;

    &:hover {
      color: #0f766e;
      text-decoration: underline;
    }
  }
}

// Error & Loading States
.error-text {
  color: #000000;
  font-size: 16px;
  text-align: center;
  padding: 20px;
  font-weight: 500;
}

.unpublished-message,
.loading-message {
  text-align: center;
  padding: 40px 20px;

  h6 {
    font-weight: 600;
    margin-bottom: 12px;
    color: #1a1a1a;
  }

  div {
    color: #666;
    line-height: 1.6;
  }
}

// Main Layout
.webStickerBody {
  padding-top: 24px !important;
  padding-left: 24px !important;
  padding-right: 32px !important;
  padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px)) !important;
  max-width: 1200px;
  margin: 0;
  width: 100%;
  box-sizing: border-box;

  &.embedded {
    max-width: 100%;
    padding: 40px 24px !important;
  }

  @media (min-width: 768px) {
    padding-top: 32px !important;
    padding-left: 40px !important;
    padding-right: 48px !important;
    padding-bottom: 80px !important;

    &.embedded {
      padding: 40px 40px !important;
    }
  }

  @media (min-width: 1024px) {
    padding-top: 40px !important;
    padding-left: 64px !important;
    padding-right: 72px !important;
    padding-bottom: 96px !important;

    &.embedded {
      padding: 40px 64px !important;
    }
  }
}

.sticker-content-wrapper {
  width: 100%;
}

.webSticker {
  display: grid;
  grid-template-columns: 1fr;
  gap: 64px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1023px) {
    gap: 32px;

    // On mobile: show right column (title, amount, CTA) before left column (description)
    .sticker-column-right { order: -1; }
    .sticker-column-left  { order: 0; }
  }

  @media (min-width: 768px) {
    gap: 80px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 96px;
  }

  // Ticket + banner: left column is hidden, so use a single centered column
  &.ticket-banner-layout {
    @media (min-width: 1024px) {
      grid-template-columns: 1fr;
      max-width: 800px;
      margin-left: auto;
      margin-right: auto;
      gap: 0;
    }
  }
}

// Left Column - Sticker Display
.sticker-column-left {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  min-width: 0;
}

.sticker-display-wrapper {
  position: relative;
}

.sticker-image-container {
  position: relative;
  background: #f9fafb;
  border-radius: 16px;
  padding: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse-shadow 2s ease-in-out infinite;
  width: 100%;
  box-sizing: border-box;

  .sticker-image {
    max-width: 100%;
    height: auto;
    display: block;
    animation: gentle-float 4s ease-in-out infinite;
  }
}

@keyframes pulse-shadow {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(20, 184, 166, 0);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(20, 184, 166, 0.1);
  }
}

@keyframes gentle-float {
  0%, 100% {
    transform: scale(1) rotate(0deg);
  }
  25% {
    transform: scale(1.05) rotate(2deg);
  }
}

@keyframes pulse-border {
  0%, 100% {
    border-color: rgba(13, 148, 136, 0.3);
  }
  50% {
    border-color: rgba(13, 148, 136, 0.5);
  }
  75% {
    transform: scale(1.05) rotate(-2deg);
  }
}

// Badges
.collect-badge {
  position: absolute;
  top: -16px;
  right: -16px;
  background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0 10px 40px rgba(13, 148, 136, 0.3);
  animation: badge-appear 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.3s backwards;
  cursor: pointer;
  transition: transform 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  .collect-badge-icon {
    width: 16px;
    height: 16px;
    display: block;
  }

  &:hover {
    transform: scale(1.1);
  }
}

@keyframes badge-appear {
  from {
    transform: scale(0) rotate(-45deg);
    opacity: 0;
  }
  to {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.glow-badge {
  position: absolute;
  bottom: -16px;
  left: -16px;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #000000;
  padding: 12px 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 10px 40px rgba(251, 191, 36, 0.4);
  animation: glow-pulse 2s ease-in-out infinite, badge-appear-rotate 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.5s backwards;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }

  .glow-badge-icon {
    width: 20px;
    height: 20px;
    color: #000000;
    flex-shrink: 0;
  }

  .glow-badge-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .glow-badge-title {
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #000000;
  }

  .glow-badge-date {
    font-size: 12px;
    font-weight: 600;
    color: #000000;
  }
}

@keyframes glow-pulse {
  0%, 100% {
    box-shadow: 0 10px 40px rgba(251, 191, 36, 0.4);
  }
  50% {
    box-shadow: 0 10px 60px rgba(251, 191, 36, 0.6);
  }
}

@keyframes badge-appear-rotate {
  from {
    transform: scale(0) rotate(45deg);
    opacity: 0;
  }
  to {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

// Responsive Tabs (Desktop Only)
.tabs-navigation {
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
  display: none; // Hidden on mobile
  gap: 32px;

  @media (min-width: 1024px) {
    display: flex; // Show on desktop
  }
}

.tab-button {
  background: none;
  border: none;
  padding: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;

  &:hover {
    color: #1a1a1a;
  }

  &.active {
    color: #0d9488;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: #0d9488;
    }
  }
}

// Responsive Helpers
.desktop-only {
  display: none;

  @media (min-width: 1024px) {
    display: flex;
  }
}

.mobile-only {
  display: block;

  @media (min-width: 1024px) {
    display: none;
  }
}

.desktop-hidden {
  @media (min-width: 1024px) {
    display: none !important;
  }
}

// Content Sections
.content-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.content-section {
  padding: 0;

  // On mobile: show borders between sections
  @media (max-width: 1023px) {
    &.details-section,
    &.creator-section,
    &.updates-section,
    &.announcements-section {
      border-bottom: 1px solid #f3f4f6;
      padding-bottom: 24px;

      &:last-child {
        border-bottom: none;
      }
    }
  }

  // On desktop: no borders (tabs handle separation)
  @media (min-width: 1024px) {
    border-bottom: none !important;
    padding-bottom: 0 !important;
  }
}

.section-heading {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;
}

.section-description {
  font-size: 14px;
  line-height: 1.6;
  color: #374151;

  p {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Creator Info Details (About Creator Tab)
.creator-info-details {
  display: flex;
  flex-direction: column;
}

.creator-details-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.creator-detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .detail-label {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .detail-value {
    font-size: 16px;
    color: #1a1a1a;
    font-weight: 500;

    a {
      color: #0d9488;
      text-decoration: none;
      transition: color 0.2s;

      &:hover {
        color: #0f766e;
        text-decoration: underline;
      }
    }
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Right Column
.sticker-column-right {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
}

// Left Column
.sticker-column-left {
  @media (max-width: 1023px) {
    padding-bottom: 100px; // Space for floating cart pill when left col is last
  }
}

// Campaign Badge
.campaign-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #ccfbf1;
  color: #0f766e;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 700;
  width: fit-content;

  .campaign-badge-dot {
    width: 8px;
    height: 8px;
    background: #14b8a6;
    border-radius: 50%;
  }

  &.is-clickable {
    cursor: pointer;
    transition: background 0.2s, transform 0.2s;

    &:hover {
      background: #99f6e4;
      transform: translateY(-1px);
    }

    &:focus-visible {
      outline: 2px solid #0d9488;
      outline-offset: 2px;
    }
  }
}

// Creator Info
.creator-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;

  &.is-clickable {
    cursor: pointer;

    &:hover {
      .creator-name {
        color: #0d9488;
      }
    }
  }
}

.creator-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #0d9488;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .creator-initial {
    color: white;
    font-weight: 700;
    font-size: 20px;
  }
}

.creator-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.creator-name {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
}

.creator-verified {
  font-size: 12px;
  color: #6b7280;
}

// Title & Price
.sticker-title {
  font-size: 30px;
  font-weight: 900;
  color: #1a1a1a;
  margin: 4px 0;
  line-height: 1.2;
}

.price-range {
  font-size: 18px;
  font-weight: 800;
  color: #0d9488;
  margin-bottom: 2px;
}

// Two-line price format
.price-with-fees {
  display: flex;
  flex-direction: column;
  gap: 2px;

  .price-amount {
    font-size: 18px;
    font-weight: 800;
    color: #0d9488;
    line-height: 1.2;
  }

  .price-label {
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
    text-transform: lowercase;
  }
}

// Charity Info
.charity-info {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 16px;

  .charity-label {
    color: #6b7280;
  }

  .charity-name {
    color: #0d9488;
    font-weight: 500;
  }

  .charity-ein {
    color: #6b7280;
  }
}

// Sweepstakes Dates
.sweepstakes-dates {
  margin-bottom: 24px;

  .date-label {
    font-size: 14px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 4px;
  }

  .date-value {
    font-size: 14px;
    color: #6b7280;
  }
}

// Amount Input Section
.amount-input-section {
  margin-bottom: 32px;
}

.amount-input-section--desktop {
  @media (max-width: 1023px) {
    display: none; // On mobile the amount section lives above Details in the left column
  }
}

.amount-input-section--mobile {
  display: none;

  @media (max-width: 1023px) {
    display: block;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 24px;
  }
}

.amount-label {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.amount-input-wrapper {
  position: relative;
  margin-bottom: 12px;

  .dollar-sign {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 18px;
    font-weight: 600;
    color: #6b7280;
  }

  .amount-input {
    width: 100%;
    padding: 16px 16px 16px 40px;
    border: 2px solid #d1d5db;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #0d9488;
      box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
    }

    &::placeholder {
      color: #d1d5db;
    }

    // Remove number input spinners
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    -moz-appearance: textfield;
  }
}

.preset-amounts {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
}

.preset-button {
  padding: 12px;
  border: none;
  border-radius: 12px;
  background: #f3f4f6;
  color: #374151;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
    transform: scale(1.05);
  }

  &.active {
    background: #0d9488;
    color: white;
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
  }
}

// Sweepstakes Slider
.sweepstakes-slider {
  margin: 32px 0;
  padding: 16px;

  .sweepstakes-prize-box {
    background: linear-gradient(135deg, rgba(67, 73, 221, 0.1) 0%, rgba(67, 73, 221, 0.05) 100%);
    border: 1px solid rgba(67, 73, 221, 0.2);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 24px;

    .prize-header {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .prize-label {
        font-size: 12px;
        font-weight: 600;
        color: #4349dd;
        text-transform: uppercase;
      }

      .prize-name {
        font-size: 16px;
        font-weight: 700;
        color: #4349dd;
      }
    }

    .prize-details {
      font-size: 12px;
      color: #6b7280;
      margin-top: 8px;
      font-style: italic;
    }
  }

  .sweepstakes-dates-row {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;

    .date-col {
      flex: 1;

      .date-label {
        font-size: 11px;
        color: #6b7280;
        margin-bottom: 4px;
      }

      .date-value {
        font-size: 13px;
        font-weight: 600;
        color: #1a1a1a;
      }
    }
  }

  .slider-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;

    .slider-label,
    .slider-value {
      font-weight: 700;
      font-size: 14px;
      color: #1a1a1a;
    }
  }

  .sweepstakes-legal {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;

    .legal-text {
      font-size: 10px;
      color: #6b7280;
      line-height: 1.5;
      margin: 0 0 8px 0;

      strong {
        font-weight: 700;
      }
    }

    .legal-details {
      font-size: 10px;
      color: #6b7280;
      line-height: 1.5;
      margin: 0 0 8px 0;
    }

    .legal-link {
      background: none;
      border: none;
      color: #0d9488;
      font-size: 10px;
      text-decoration: underline;
      cursor: pointer;
      padding: 0;

      &:hover {
        text-decoration: none;
      }
    }
  }

  .dots-container {
    display: flex;
    justify-content: space-between;
    margin-top: 16px;

    .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #d1d5db;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: #9ca3af;
      }

      &.active {
        background: #0d9488;
      }
    }
  }
}

// Variants Picker
.variants-picker {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin: 0 0 24px 0;
  align-items: start;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }

  // Add-on tiers stay single column — cards have variable content (bullets, option groups)
  &.addons-tier-section {
    @media (min-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
}

.variant-card {
  cursor: pointer;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  transition: all 0.3s;
  position: relative;
  background: white;

  &:hover {
    border-color: rgba(13, 148, 136, 0.3);
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.08);
  }

  &.selected {
    border-color: #0d9488;
    background: #f0fdfa;
  }

  // Conditional styling for special ticket types
  &.variant-donation {
    border-color: rgba(13, 148, 136, 0.3);
    background: white;
    animation: pulse-border 2s ease-in-out infinite;
  }

  &.variant-glow {
    border-color: rgba(255, 219, 15, 0.6);
    background: white;
  }

  &.variant-sweepstakes {
    border-color: rgba(67, 73, 221, 0.4);
    background: white;
  }

  &--sold-out {
    opacity: 0.6;
    cursor: not-allowed;

    &:hover {
      border-color: #e5e7eb;
      box-shadow: none;
    }
  }

  .variant-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .variant-title {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
  }

  .variant-select {
    width: 68px;
    height: 48px;
    border-radius: 8px;
    border: none;
    background: #f3f4f6;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    cursor: pointer;
    appearance: none;
    background-image: url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 320 512%27%3E%3Cpath fill=%27%23374151%27 d=%27M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z%27/%3E%3C/svg%3E');
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 10px;
    padding-right: 32px;
  }

  .variant-cost {
    font-size: 24px;
    font-weight: 900;
    color: #1a1a1a;
    margin-bottom: 8px;

    .price-amount {
      font-size: 24px;
      font-weight: 900;
      color: #1a1a1a;
      line-height: 1.2;
    }

    .price-label {
      font-size: 11px;
      font-weight: 500;
      color: #6b7280;
      text-transform: lowercase;
      margin-top: 2px;
    }
  }

  .variant-description {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.6;
  }
}

.in-cart-panel--enter,
.cart-floating-in-cart--enter {
  animation: in-cart-reveal-in 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes in-cart-reveal-in {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

// Action Buttons
.action-buttons {
  margin: 16px 0 32px 0;

  @media (max-width: 1023px) {
    display: none; // Fixed bottom bar handles all CTAs on mobile
  }
}

.in-cart-panel,
.cart-floating-in-cart {
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  border-radius: 12px;
}

.in-cart-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.in-cart-summary {
  font-size: 13px;
  font-weight: 600;
  color: #0f766e;
  white-space: nowrap;
}

.in-cart-controls {
  flex-shrink: 0;
}

.in-cart-qty {
  display: inline-flex;
  align-items: center;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  overflow: hidden;
}

.in-cart-qty-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: white;
  color: #0d9488;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #f0fdfa;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.in-cart-qty-value {
  min-width: 24px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}

.in-cart-price {
  display: inline-flex;
  align-items: center;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 8px;
  height: 28px;
}

.in-cart-price-prefix {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin-right: 2px;
}

.in-cart-price-input {
  border: none;
  outline: none;
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  width: 56px;
  background: transparent;
}

.in-cart-actions {
  display: flex;
  gap: 6px;

  &--compact {
    .in-cart-checkout,
    .in-cart-continue {
      flex: 1;
      min-width: 0;
    }
  }
}

.in-cart-checkout {
  padding: 8px 12px;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(13, 148, 136, 0.2);
  transition: transform 0.15s;

  &:hover {
    transform: translateY(-1px);
  }
}

.in-cart-continue {
  padding: 8px 12px;
  background: white;
  color: #0d9488;
  border: 1.5px solid #0d9488;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f0fdfa;
  }
}

.cart-floating-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;

  .cart-floating-in-cart {
    margin-bottom: 0;
    padding: 10px 12px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);

    .in-cart-row {
      margin-bottom: 8px;
    }
  }

  .cart-floating-pill {
    padding: 12px 16px;
  }
}

.primary-action-button {
  width: 100%;
  padding: 16px;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(13, 148, 136, 0.3);
  transition: all 0.2s;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    animation: shimmer 3s linear infinite;
  }

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 12px 40px rgba(13, 148, 136, 0.4);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled,
  &.button-loading {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 200%;
  }
}

// Visually-disabled state for Campaign CTA (not truly disabled — click still fires to scroll/highlight)
.button-visually-disabled {
  opacity: 0.55;
  cursor: default;

  &::before {
    display: none; // No shimmer when disabled
  }
}

// Amount input highlight + shake when user presses CTA without entering amount
.amount-input-highlight {
  .amount-input {
    border-color: #ef4444 !important;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18) !important;
    animation: amount-shake 0.45s ease;
  }
}

@keyframes amount-shake {
  0%, 100% { transform: translateX(0); }
  20%       { transform: translateX(-6px); }
  40%       { transform: translateX(6px); }
  60%       { transform: translateX(-4px); }
  80%       { transform: translateX(4px); }
}

.campaign-cta-highlight {
  animation: campaign-cta-pulse 0.45s ease 2;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.35), 0 4px 12px rgba(13, 148, 136, 0.3) !important;
}

@keyframes campaign-cta-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.03); }
}

.secondary-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.secondary-actions-row {
  display: flex;
  gap: 8px;
}

.secondary-action-button {
  flex: 1;
  min-width: 0;
  padding: 12px 8px;

  &--full {
    flex: none;
    width: 100%;
  }
  border: 2px solid #d1d5db;
  background: white;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;

  img,
  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: #f9fafb;
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }
}

// Embed: back nav + Donate + circular share inline
.webStickerBody.embedded {
  .embed-back-header {
    margin: -8px 0 16px;
  }

  .embed-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px 6px 8px;
    border: none;
    border-radius: 9999px;
    background: transparent;
    color: #374151;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:hover {
      background: #f3f4f6;
    }

    &:active {
      background: #e5e7eb;
    }
  }

  .embed-back-icon {
    width: 18px;
    height: 18px;
    transform: rotate(180deg);
    opacity: 0.7;
  }

  .embed-action-row,
  .embed-fixed-action-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .embed-action-row .primary-action-button {
    flex: 1;
    width: auto;
    min-width: 0;
    -webkit-tap-highlight-color: transparent;

    &:active {
      transform: none;
    }
  }

  .embed-share-btn {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border: 2px solid #d1d5db;
    border-radius: 50%;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    -webkit-tap-highlight-color: transparent;
    transition: border-color 0.2s;

    img {
      width: 18px;
      height: 18px;
    }

    &:hover {
      border-color: #9ca3af;
    }

    &:active,
    &:focus {
      background: white;
      outline: none;
      transform: none;
    }
  }

  // Hide secondary-actions row when only leftover items aren't shown in embed
  .secondary-actions:empty {
    display: none;
  }
}

// Detail Sections (for sweepstakes extended info)
.sweepstakes-details {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.detail-section {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  .detail-heading {
    font-size: 14px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 8px;
  }

  .detail-content {
    font-size: 14px;
    line-height: 1.6;
    color: #6b7280;
  }

  iframe {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-top: 8px;
  }
}

// Event date/time — teal inline row (Ticket)
.event-datetime {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;

  .event-datetime-icon {
    width: 20px;
    height: 20px;
    color: #0d9488;
    flex-shrink: 0;
  }

  .event-datetime-text {
    font-size: 15px;
    font-weight: 600;
    color: #0d9488;
  }
}

// Event date/time — grid layout (non-ticket)
.event-datetime-grid {
  margin-bottom: 24px;

  .event-value {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 4px;
  }

  .event-label {
    font-size: 14px;
    color: #6b7280;
  }
}

// Unified section heading (Select Tickets, Add-Ons, Gallery, FAQ)
.section-heading {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 28px 0 12px 0;
  letter-spacing: 0;
  text-transform: none;
}

// Ticket tier card header (title + subtitle on top)
.variant-info {
  margin-bottom: 12px;

  .variant-title {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a1a;
    display: block;
  }

  .variant-description {
    font-size: 13px;
    color: #6b7280;
    margin-top: 2px;
    line-height: 1.4;
  }
}

// Variant row with stepper + price
.variant-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  .variant-stepper {
    display: flex;
    align-items: center;
    gap: 0;
    flex-shrink: 0;

    .stepper-btn {
      width: 36px;
      height: 36px;
      border: none;
      background: #f3f4f6;
      border-radius: 50%;
      font-size: 20px;
      font-weight: 600;
      color: #1a1a1a;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      transition: all 0.2s;
      padding: 0;

      &:first-child {
        // Minus button - gray background
        background: #f3f4f6;

        &:hover:not(:disabled) {
          background: #e5e7eb;
        }
      }

      &:last-child {
        // Plus button - primary background
        background: #0d9488;
        color: white;

        &:hover:not(:disabled) {
          background: #0f766e;
        }
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      &:active {
        background: #f0fdfa;
      }
    }

    .stepper-count {
      min-width: 32px;
      text-align: center;
      font-size: 15px;
      font-weight: 700;
      color: #1a1a1a;
    }
  }

  .variant-cost {
    font-size: 16px;
    font-weight: 800;
    color: #1a1a1a;
    flex-shrink: 0;
    min-width: 60px;
    text-align: right;

    .price-amount {
      font-size: 16px;
      font-weight: 800;
      color: #1a1a1a;
      line-height: 1.2;
    }

    .price-label {
      font-size: 10px;
      font-weight: 500;
      color: #6b7280;
      text-transform: lowercase;
      margin-top: 1px;
    }
  }
}

// Add To Cart button
.btn-add-to-cart {
  width: 100%;
  padding: 16px;
  background: #e5e7eb;
  color: #9ca3af;
  border: none;
  border-radius: 9999px;
  font-size: 16px;
  font-weight: 700;
  cursor: not-allowed;
  transition: all 0.2s;
  margin-bottom: 12px;

  &--active,
  &.btn-add-to-cart--active {
    background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
    color: white;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(13, 148, 136, 0.3);

    &:hover {
      transform: scale(1.02);
    }
  }

  &:disabled:not(.btn-add-to-cart--active),
  &.button-loading {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
}

.btn-browse-events {
  width: 100%;
  padding: 16px;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 8px 24px rgba(13, 148, 136, 0.3);
  }
}

.btn-my-tickets {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 14px 16px;
  background: #fff;
  color: #0d9488;
  border: 2px solid #0d9488;
  border-radius: 9999px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s;

  &:hover {
    background: #f0fdfa;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.2);
  }

  .ticket-btn-icon {
    width: 18px;
    height: 18px;
    color: #0d9488;
  }
}

.btn-manage-checkin {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 14px 16px;
  background: #fff;
  color: #0d9488;
  border: 2px solid #0d9488;
  border-radius: 9999px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s;

  .checkin-btn-icon {
    width: 18px;
    height: 18px;
    filter: invert(40%) sepia(80%) saturate(400%) hue-rotate(140deg);
  }

  &:hover {
    background: #f0fdf9;
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.15);
  }
}

// Floating Cart / CTA (Mobile Only)
.cart-floating-action {
  display: none;

  @media (max-width: 767px) {
    display: block;
    position: fixed;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    left: 16px;
    right: 16px;
    z-index: 30;
    pointer-events: none;

    > * {
      pointer-events: auto;
    }
  }
}

.cart-floating-row {
  display: flex;
  align-items: center;
  gap: 12px;

  .cart-floating-pill {
    flex: 1;
    min-width: 0;
  }

  .embed-share-btn {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border: 2px solid #d1d5db;
    border-radius: 50%;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    -webkit-tap-highlight-color: transparent;

    img {
      width: 18px;
      height: 18px;
    }
  }
}

.cart-floating-pill {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 14px 20px;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(13, 148, 136, 0.35);
  transition: transform 0.2s, box-shadow 0.2s;
  -webkit-tap-highlight-color: transparent;

  &:hover:not(:disabled):not(.button-visually-disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 28px rgba(13, 148, 136, 0.4);
  }

  &:active:not(:disabled):not(.button-visually-disabled) {
    transform: translateY(0);
  }

  &:disabled,
  &.is-loading {
    opacity: 0.65;
    cursor: not-allowed;
  }

  &.button-visually-disabled {
    opacity: 0.55;
    cursor: default;
  }

  .pill-primary {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
  }

  .pill-secondary {
    font-size: 12px;
    font-weight: 500;
    opacity: 0.92;
    line-height: 1.2;
  }
}

// Event Banner
.event-banner {
  margin-left: -24px;
  margin-right: -32px;
  max-height: 280px;
  overflow: hidden;
  border-radius: 0;
  margin-bottom: 24px;
  position: relative;

  .banner-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .floating-sticker-badge {
    position: absolute;
    top: -64px;
    left: 16px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    border-radius: 9999px;
    padding: 4px 8px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    animation: float-sticker 3s ease-in-out infinite;
    transition: transform 0.2s;

    &:hover {
      transform: translateY(-2px);
    }

    .sticker-thumbnail {
      width: 32px;
      height: 32px;
      object-fit: contain;
      transform: scale(1.5);
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
      mix-blend-mode: normal;
    }

    .badge-text {
      font-size: 10px;
      font-weight: 700;
      color: #1a1a1a;
      white-space: nowrap;
      padding-right: 8px;
      margin-left: 8px;
    }
  }

  .banner-overlay-info {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    padding: 10px 16px;

    .overlay-content {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 16px;
    }

    .date-time-info {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 6px;

      .date-text,
      .time-text {
        font-size: 12px;
        font-weight: 500;
        color: white;
      }

      .separator {
        color: rgba(255, 255, 255, 0.7);
        font-size: 12px;
      }
    }

    .location-info {
      flex: 1;

      .location-text {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.9);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: block;
      }
    }
  }
}

@keyframes float-sticker {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

// Event Location (physical)
.event-location {
  margin-bottom: 12px;

  .location-icon-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #0d9488;
    font-weight: 600;
  }

  .location-icon {
    width: 18px;
    height: 18px;
    color: #0d9488;
    flex-shrink: 0;
  }
}

.virtual-link-cta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #f0fdfa;
  border: 1.5px solid #0d9488;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.15s;
  margin-bottom: 12px;

  &:hover {
    background: #ccfbf1;
  }

  .virtual-link-icon {
    width: 18px;
    height: 18px;
    color: #0d9488;
    flex-shrink: 0;
  }

  .virtual-link-content {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .virtual-link-label {
    font-size: 14px;
    font-weight: 700;
    color: #0d9488;
    line-height: 1.2;
  }

  .virtual-link-domain {
    font-size: 12px;
    color: #0f766e;
    line-height: 1.2;
  }
}

// Inline label inside addon cards (donation amount selectors)
.section-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 8px;
}

// Wrapper for the full add-ons block
.combined-addons-wrapper {
  margin-top: 8px;
}

// Variant extras
.variant-free {
  color: #0d9488 !important;
}

.variant-card--sold-out {
  opacity: 0.55;
  pointer-events: none;
}

.variant-availability {
  margin-top: 4px;
}

.badge-sold-out {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  color: #dc2626;
  background: #fee2e2;
  border-radius: 10px;
  padding: 2px 8px;
}

.badge-remaining {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  color: #b45309;
  background: #fef3c7;
  border-radius: 10px;
  padding: 2px 8px;
}

.variant-included {
  margin-top: 8px;

  .included-line {
    font-size: 13px;
    color: #374151;
    padding: 2px 0;

    &::before {
      color: #0d9488;
    }
  }
}

.variant-option-picker {
  margin-top: 12px;
  position: relative;
  z-index: 1;

  .option-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    margin-bottom: 6px;
  }

  .option-select-el {
    width: 100%;

    :deep(.el-input__wrapper) {
      padding: 10px 12px;
      border: 1.5px solid #d1d5db;
      border-radius: 8px;
      box-shadow: none;
      background: #fff;
      transition: all 0.2s;

      &:hover {
        border-color: #9ca3af;
      }
    }

    :deep(.el-input__inner) {
      font-size: 14px;
      color: #1a1a1a;
      height: auto;
    }

    :deep(.el-input.is-focus .el-input__wrapper) {
      border-color: #0d9488;
      box-shadow: none;
    }

    :deep(.el-input__suffix) {
      color: #374151;
    }
  }

  // Old native select styles (kept for reference/fallback)
  .option-select {
    width: 100%;
    padding: 10px 12px;
    border: 1.5px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    color: #1a1a1a;
    background: #fff;
    cursor: pointer;
    appearance: none;
    background-image: url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 320 512%27%3E%3Cpath fill=%27%23374151%27 d=%27M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z%27/%3E%3C/svg%3E');
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 10px;
    padding-right: 32px;
    position: relative;

    &:focus {
      outline: none;
      border-color: #0d9488;
    }
  }
}

// Dropdown popper styling
:deep(.variant-option-popper) {
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  .el-select-dropdown__item {
    font-size: 14px;
    color: #1a1a1a;
    padding: 10px 12px;

    &:hover {
      background: #f3f4f6;
    }

    &.selected {
      color: #0d9488;
      font-weight: 600;
    }
  }
}

// Advanced Option Groups
.variant-option-groups {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;

  .option-group {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    .option-group-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;

      .option-group-title {
        font-size: 14px;
        font-weight: 600;
        color: #1a1a1a;
      }

      .option-required-badge {
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        color: #0d9488;
        background: rgba(13, 148, 136, 0.1);
        padding: 2px 6px;
        border-radius: 4px;
      }

      .option-multi-hint {
        font-size: 10px;
        font-weight: 500;
        color: #6b7280;
      }
    }

    .option-buttons-grid {
      display: grid;
      gap: 8px;

      .option-button {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px;
        border: 2px solid #e5e7eb;
        border-radius: 12px;
        background: white;
        cursor: pointer;
        transition: all 0.2s;
        text-align: left;

        &:hover {
          border-color: #d1d5db;
          background: #f9fafb;
        }

        &.option-selected {
          border-color: #0d9488;
          background: rgba(13, 148, 136, 0.05);
        }

        .option-checkbox {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 2px solid #d1d5db;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;

          .check-icon {
            width: 12px;
            height: 12px;
            color: white;
          }
        }

        &.option-selected .option-checkbox {
          border-color: #0d9488;
          background: #0d9488;
        }

        .option-content {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-left: 12px;

          .option-name {
            font-size: 14px;
            font-weight: 500;
            color: #1a1a1a;
          }

          .option-price {
            font-size: 14px;
            font-weight: 600;
            color: #6b7280;
            text-align: right;

            &.option-price-free {
              color: #0d9488;
            }

            .price-amount {
              font-size: 14px;
              font-weight: 600;
              color: #6b7280;
              line-height: 1.2;
            }

            .price-label {
              font-size: 10px;
              font-weight: 500;
              color: #9ca3af;
              text-transform: lowercase;
              margin-top: 1px;
            }
          }
        }
      }
    }
  }
}

// Add-ons section
.addons-section {
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.addons-tier-section {
  margin-top: 0;
}

.addon-card {
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  padding-top: 20px;
  background: #fafafa;
  position: relative;
  transition: all 0.2s;

  // Type badge at top-left corner
  .addon-type-badge {
    position: absolute;
    top: -8px;
    left: 16px;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.5px;
    color: white;

    &.addon-badge-donation {
      background: #0d9488;
    }

    &.addon-badge-sweepstakes {
      background: #4349dd;
    }

    &.addon-badge-glow {
      background: #fbbf24;
      color: #1a1a1a;
    }
  }

  // Purple theme for sweepstakes
  &:has(.sweepstakes-title) {
    border-color: rgba(67, 73, 221, 0.4);
    background: #ffffff;

    &:hover {
      border-color: rgba(67, 73, 221, 0.6);
    }
  }

  // Yellow/amber theme for glow
  &:has(.glow-title) {
    border-color: rgba(255, 219, 15, 0.6);
    background: #ffffff;

    &:hover {
      border-color: rgba(255, 219, 15, 0.8);
    }
  }

  .addon-title {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 10px;

    &.sweepstakes-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 16px;
      margin-bottom: 12px;

      .sweepstakes-icon {
        width: 18px;
        height: 18px;
        color: #4349dd;
        flex-shrink: 0;
      }
    }
  }

  .addon-description {
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 14px;
    line-height: 1.5;
  }

  .addon-causes {
    margin-bottom: 10px;

    .addon-cause-item {
      font-size: 13px;
      color: #6b7280;
      padding: 2px 0;
    }
  }

  .addon-amount {
    font-size: 18px;
    font-weight: 800;
    color: #0d9488;
    display: block;
    margin-bottom: 10px;
  }

  .addon-glow-meta {
    margin-bottom: 10px;
  }

  // Donation amount section styling
  .addon-donation-fixed,
  .addon-donation-suggested,
  .addon-donation-custom {
    margin-top: 12px;
    margin-bottom: 14px;
  }

  .addon-donation-custom {
    .donation-input-wrapper {
      position: relative;

      .dollar-prefix {
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 14px;
        font-weight: 700;
        color: #6b7280;
        pointer-events: none;
      }
    }
  }

  .suggested-amounts {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 12px;

    .suggested-btn {
      padding: 12px 16px;
      border: 2px solid #d1d5db;
      border-radius: 8px;
      background: white;
      font-size: 14px;
      font-weight: 700;
      color: #374151;
      cursor: pointer;
      transition: all 0.2s;
      text-align: center;

      &:hover {
        border-color: #0d9488;
        background: rgba(13, 148, 136, 0.05);
      }

      &.active {
        border-color: #0d9488;
        background: rgba(13, 148, 136, 0.1);
        color: #0d9488;
      }
    }
  }

  .custom-donation-input {
    margin-top: 12px;

    label {
      display: block;
      font-size: 12px;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 6px;
    }

    .donation-input-wrapper {
      position: relative;

      .dollar-prefix {
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 14px;
        font-weight: 700;
        color: #6b7280;
        pointer-events: none;
      }
    }
  }

  .addon-input {
    width: 100%;
    padding: 12px 14px 12px 32px;
    border: 2px solid #d1d5db;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #0d9488;
      box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
    }

    -moz-appearance: textfield;

    &::placeholder {
      color: #d1d5db;
    }
  }

  .addon-checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #374151;
    cursor: pointer;
    user-select: none;

    input[type='checkbox'] {
      width: 16px;
      height: 16px;
      accent-color: #0d9488;
      cursor: pointer;
    }
  }

  .sweepstakes-loading {
    padding: 0.5rem 0;

    .skeleton-line {
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: shimmer 1.2s infinite;
      border-radius: 6px;
      margin-bottom: 0.5rem;

      &.skeleton-title {
        height: 1.25rem;
        width: 60%;
      }

      &.skeleton-short {
        height: 1rem;
        width: 40%;
      }
    }

    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  }

  // Sweepstakes-specific styles
  .sweepstakes-header {
    margin-bottom: 12px;

    .sweepstakes-title {
      font-size: 18px;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 6px;
    }

    .sweepstakes-pricing {
      display: flex;
      align-items: baseline;
      gap: 6px;
      margin-bottom: 8px;

      .price-main {
        font-size: 32px;
        font-weight: 800;
        color: #4349dd;
        line-height: 1;
      }

      .price-breakdown {
        font-size: 14px;
        color: #6b7280;
        font-weight: 500;
      }
    }
  }

  .sweepstakes-prize-box {
    background: rgba(67, 73, 221, 0.08);
    border: 1px solid rgba(67, 73, 221, 0.2);
    border-radius: 12px;
    padding: 14px;
    margin-bottom: 16px;

    .prize-label {
      font-size: 11px;
      font-weight: 700;
      color: #4349dd;
      letter-spacing: 0.5px;
      margin-bottom: 6px;
    }

    .prize-title {
      font-size: 15px;
      font-weight: 700;
      color: #4349dd;
      margin-bottom: 4px;
      line-height: 1.3;
    }

    .prize-details {
      font-size: 12px;
      color: #6b7280;
      font-style: italic;
      margin-top: 6px;
      line-height: 1.4;
    }
  }

  .sweepstakes-dates-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
    font-size: 12px;

    .date-column {
      .date-label {
        color: #9ca3af;
        margin-bottom: 4px;
        font-size: 11px;
      }

      .date-value {
        font-weight: 600;
        color: #1a1a1a;
        font-size: 12px;
        line-height: 1.3;
      }

      &:last-child {
        text-align: right;
      }
    }
  }

  .sweepstakes-legal-box {
    background: #fafafa;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 16px;

    .legal-bold {
      font-size: 10px;
      font-weight: 700;
      color: #1a1a1a;
      margin: 0 0 6px 0;
      line-height: 1.4;
    }

    .legal-text {
      font-size: 10px;
      color: #6b7280;
      margin: 0 0 8px 0;
      line-height: 1.4;
    }

    .legal-rules-btn {
      font-size: 10px;
      color: #4349dd;
      text-decoration: underline;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-weight: 500;

      &:hover {
        text-decoration: none;
      }
    }
  }

  .sweepstakes-entry-slider {
    margin-bottom: 8px;

    .slider-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;

      .slider-label {
        font-size: 14px;
        font-weight: 600;
        color: #1a1a1a;
      }

      .slider-value {
        font-size: 24px;
        font-weight: 700;
        color: #4349dd;
        line-height: 1;
      }
    }

    .sweepstakes-range {
      width: 100%;
      height: 8px;
      border-radius: 4px;
      background: #e5e7eb;
      outline: none;
      appearance: none;
      cursor: pointer;
      margin-bottom: 8px;

      &::-webkit-slider-thumb {
        appearance: none;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #4349dd;
        cursor: pointer;
        border: 4px solid white;
        box-shadow: 0 2px 8px rgba(67, 73, 221, 0.4);
      }

      &::-moz-range-thumb {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #4349dd;
        cursor: pointer;
        border: 4px solid white;
        box-shadow: 0 2px 8px rgba(67, 73, 221, 0.4);
      }
    }

    .slider-labels {
      display: flex;
      justify-content: space-between;

      .slider-min,
      .slider-max {
        font-size: 12px;
        color: #9ca3af;
        font-weight: 500;
      }
    }
  }

  .slider-footer-pricing {
    font-size: 12px;
    color: #6b7280;
    text-align: center;
    margin-top: 8px;
    padding-top: 8px;
  }

  // Multi-charity donation styles
  .donation-charities {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 16px 0 14px 0;

    .charities-header {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 4px;

      .charities-title {
        font-size: 14px;
        font-weight: 600;
        color: #374151;
      }

      .charities-hint {
        font-size: 10px;
        font-weight: 500;
        color: #9ca3af;
      }
    }

    .charity-card {
      border: 2px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: white;
      transition: all 0.2s;
      cursor: pointer;

      &:hover {
        border-color: #cbd5e1;
        background: #f9fafb;
      }

      &.charity-selected {
        border-color: #0d9488;
        background: rgba(13, 148, 136, 0.05);
      }

      .charity-label {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        cursor: pointer;
        width: 100%;

        .charity-checkbox {
          width: 20px;
          height: 20px;
          margin-top: 2px;
          accent-color: #0d9488;
          cursor: pointer;
          flex-shrink: 0;
          border-radius: 50%;
        }

        .charity-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;

          .charity-name {
            font-size: 14px;
            font-weight: 600;
            color: #1a1a1a;
          }

          .charity-ein {
            font-size: 11px;
            color: #9ca3af;
          }
        }
      }

    }
  }

  .donation-summary-message {
    margin-top: 12px;
    padding: 10px 12px;
    background: rgba(13, 148, 136, 0.1);
    border-radius: 8px;
    border-left: 3px solid #0d9488;

    .summary-message-text {
      font-size: 12px;
      font-weight: 500;
      color: #0d9488;
      margin: 0;
      line-height: 1.5;
    }
  }

  // Glow add-on specific styles
  .glow-addon-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;

    .glow-addon-info {
      flex: 1;

      .glow-title {
        font-size: 16px;
        font-weight: 700;
        color: #1a1a1a;
        margin-bottom: 4px;
      }

      .glow-price {
        font-size: 22px;
        font-weight: 800;
        color: #f59e0b;

        &.glow-free {
          color: #10b981;
        }
      }
    }

    .glow-register-btn {
      flex-shrink: 0;
      padding: 10px 24px;
      background: linear-gradient(to right, #fbbf24, #f59e0b);
      color: #1a1a1a;
      border: none;
      border-radius: 9999px;
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s;
      box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);

      &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
      }

      &:active {
        transform: translateY(0);
      }
    }

    .glow-registered-btn {
      flex-shrink: 0;
      padding: 10px 24px;
      background: #d1fae5;
      color: #065f46;
      border: 1.5px solid #6ee7b7;
      border-radius: 9999px;
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: #fee2e2;
        color: #991b1b;
        border-color: #fca5a5;
      }
    }
  }
}

// Gallery Section Main (Right Column)
.gallery-section-main {
  margin: 8px 0 24px 0;
  border-top: 1px solid #e5e7eb;
  padding-top: 8px;
}

// FAQ Accordion Section (Figma Design)
.faq-accordion-section {
  margin: 32px 0 24px 0;
  border-top: 1px solid #e5e7eb;
  padding-top: 24px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .section-heading {
      margin: 0;
    }

    .message-host-link {
      display: flex;
      align-items: center;
      gap: 4px;
      background: rgba(31, 190, 175, 0.1);
      border: none;
      color: #1fbeaf;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      padding: 4px 10px;
      border-radius: 9999px;
      transition: all 0.2s;

      &:hover {
        background: rgba(31, 190, 175, 0.2);
      }

      .message-icon {
        width: 14px;
        height: 14px;
      }
    }
  }

  .faq-items {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .faq-item {
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: white;
      overflow: hidden;
      transition: border-color 0.2s;

      &:hover {
        border-color: #d1d5db;
      }

      .faq-question-btn {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        background: none;
        border: none;
        cursor: pointer;
        text-align: left;
        transition: background 0.2s;

        &:hover {
          background: #f9fafb;
        }

        .faq-question-text {
          font-size: 14px;
          font-weight: 600;
          color: #1a1a1a;
          flex: 1;
          padding-right: 16px;
        }

        .faq-chevron {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
          transition: transform 0.2s;
          opacity: 0.6;

          &.chevron-rotated {
            transform: rotate(180deg);
          }
        }
      }

      .faq-answer-wrapper {
        animation: slideDown 0.3s ease-out;

        .faq-answer {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.625;
          padding: 0 16px 16px 16px;
        }
      }
    }
  }
}

@keyframes slideDown {
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: 500px;
    opacity: 1;
  }
}

// Gallery grid
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  .gallery-item {
    border-radius: 8px;
    overflow: hidden;
    background: #f3f4f6;
    position: relative;
    cursor: pointer;

    img,
    video {
      width: 100%;
      height: 140px;
      object-fit: cover;
      display: block;
      transition: transform 0.2s;
    }

    &:hover img,
    &:hover video {
      transform: scale(1.04);
    }

    &__zoom-hint {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.35);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.2s;

      img {
        width: 28px;
        height: 28px;
        filter: brightness(0) invert(1);
        object-fit: contain;
        transform: none;
      }
    }

    &:hover &__zoom-hint {
      opacity: 1;
    }
  }
}

// Lightbox
.c-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 16px;
    flex-shrink: 0;
  }

  &__zoom-btn {
    background: rgba(255, 255, 255, 0.12);
    border: none;
    color: #fff;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
    transition: background 0.15s;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.25);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }

  &__zoom-label {
    color: #ddd;
    font-size: 13px;
    min-width: 46px;
    text-align: center;
  }

  &__close {
    background: rgba(255, 255, 255, 0.12);
    border: none;
    color: #fff;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 22px;
    line-height: 1;
    margin-left: 8px;
    transition: background 0.15s;

    &:hover {
      background: rgba(220, 50, 50, 0.6);
    }
  }

  &__stage {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;
    padding: 16px;
  }

  &__media {
    max-width: 90vw;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 6px;
    transform-origin: center center;
    transition: transform 0.2s ease;
    display: block;
  }
}

.c-lightbox-fade-enter-active,
.c-lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}

.c-lightbox-fade-enter-from,
.c-lightbox-fade-leave-to {
  opacity: 0;
}

// Announcements
.announcements-loading {
  padding: 24px;
  text-align: center;
  color: #6b7280;
}

.announcements-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.announcement-item {
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;

  .announcement-subject {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 4px;
  }

  .announcement-date {
    font-size: 12px;
    color: #9ca3af;
    margin-bottom: 8px;
  }

  .announcement-body {
    font-size: 14px;
    color: #4b5563;
    line-height: 1.6;
    white-space: pre-wrap;
  }
}

.announcements-empty {
  padding: 24px;
  text-align: center;
  color: #6b7280;
}

// Choose Charity Dialog
:deep(.charity-dialog) {
  border-radius: 20px;
  overflow: hidden;

  .el-dialog__header {
    text-align: center;
    font-weight: 700;
    border-bottom: 1px solid #f3f4f6;
    padding: 20px 24px;
    margin-bottom: 0;
  }

  .el-dialog__title {
    color: #1a1a1a;
    font-size: 18px;
    font-weight: 700;
  }

  .el-dialog__body {
    padding: 20px 24px 0;
    max-height: 52vh;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #d1d5db transparent;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 4px;
    }
  }

  .el-dialog__footer {
    padding: 16px 24px 24px;
    border-top: 1px solid #f3f4f6;
  }
}

.charity-image {
  position: absolute;
  top: 18px;
  left: 16px;
  width: 28px;
  height: 28px;
  opacity: 0.7;
}

.charity-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #9ca3af;
  margin-bottom: 4px;
}

.charity-suggestions {
  display: flex;
  flex-direction: column;
  width: 100%;

  .charity-checkbox-group {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  > div {
    width: 100%;
  }

  .charity-row {
    padding: 13px 0;
  }

  .charity-suggestion {
    width: 100%;
    display: flex;
    align-items: center;

    :deep(.el-radio__inner) {
      width: 20px;
      height: 20px;
      border-color: #d1d5db;
    }

    :deep(.el-radio__input.is-checked .el-radio__inner) {
      background: #0d9488;
      border-color: #0d9488;
    }

    :deep(.el-radio__label) {
      white-space: normal;
      word-break: break-word;
      font-size: 15px;
      font-weight: 500;
      color: #1a1a1a;
      line-height: 1.4;
    }

    :deep(.el-radio__input.is-checked + .el-radio__label) {
      color: #0d9488;
      font-weight: 600;
    }
  }

  .hr-color {
    border: none;
    border-top: 1px solid #f3f4f6;
    margin: 0;
    width: 100%;
    display: block;
  }
}

.charity-continue-btn {
  width: 100%;
  padding: 16px;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(13, 148, 136, 0.3);
  transition: all 0.2s;
  letter-spacing: 0.01em;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 32px rgba(13, 148, 136, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
  }
}

.login-dialog-content {
  padding: 10px 0;
  text-align: center;

  p {
    margin-bottom: 20px;
    color: #374151;
  }
}

.login-buttons {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

:deep(.login-dialog) {
  .el-dialog__header {
    text-align: center;
    font-weight: 700;
    border-bottom: 2px solid #0d9488;
  }

  .el-dialog__title {
    color: #0d9488;
  }
}

.primary-btn {
  background-color: #0d9488 !important;
  border-color: #0d9488 !important;

  &:hover {
    background-color: #0f766e !important;
    border-color: #0f766e !important;
  }
}

.secondary-btn {
  color: #0d9488 !important;
  border-color: #0d9488 !important;

  &:hover {
    background-color: #f0fdfa !important;
  }
}

.sweepstakes-dialog-content {
  padding: 10px 0;
  text-align: center;

  p {
    margin-bottom: 8px;
    color: #374151;
  }

  b {
    display: block;
    margin-bottom: 20px;
    color: #1a1a1a;
  }
}

.sweepstakes-buttons {
  margin-top: 20px;
}

:deep(.sweepstakes-dialog) {
  .el-dialog__header {
    text-align: center;
    font-weight: 700;
    border-bottom: 2px solid #0d9488;
  }

  .el-dialog__title {
    color: #0d9488;
  }
}

@media screen and (max-width: 860px) {
  :deep(.el-dialog) {
    width: 90%;
  }
}

@media screen and (max-width: 500px) {
  :deep(.charity-dialog),
  :deep(.login-dialog),
  :deep(.sweepstakes-dialog),
  :deep(.share-modal) {
    width: 95% !important;
  }
}

// Share Modal
:deep(.share-modal) {
  .el-dialog__header {
    text-align: center;
    font-weight: 700;
    border-bottom: 2px solid #0d9488;
  }

  .el-dialog__title {
    color: #0d9488;
  }

  .el-dialog__body {
    padding: 0;
  }
}

.share-modal-content {
  display: flex;
  flex-direction: column;
}

.qr-code-section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 24px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.share-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(.disabled) {
    background-color: #f9fafb;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.share-option-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #0d9488;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.share-option-text {
  font-size: 16px;
  font-weight: 500;
  color: #1a1a1a;
}

// My Tickets Dialog
:deep(.my-tickets-dialog) {
  .el-dialog__header {
    text-align: center;
    font-weight: 700;
    border-bottom: 2px solid #0d9488;
  }

  .el-dialog__title {
    color: #0d9488;
  }

  .el-dialog__body {
    padding: 24px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.my-tickets-content {
  min-height: 200px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  gap: 1rem;

  .spinner {
    width: 48px;
    height: 48px;
    border: 5px solid #e0e0e0;
    border-top-color: #0d9488;
    border-radius: 50%;
    animation: spin 0.9s linear infinite;
  }

  .loading-text {
    font-size: 0.95rem;
    color: #6b7280;
    margin: 0;
    text-align: center;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.tickets-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.ticket-card {
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  .ticket-qr {
    background: #f9fafb;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px;
    border-bottom: 2px dashed #e5e7eb;
  }

  .ticket-info {
    padding: 16px;
  }

  .ticket-tier {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 4px;
  }

  .ticket-option {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 6px;
  }

  .ticket-seat {
    font-size: 12px;
    color: #555;
    margin-bottom: 10px;
  }

  .ticket-status {
    margin-top: 4px;
  }

  .checked-in {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #0d9488;
    font-size: 12px;
    font-weight: 600;

    .status-icon {
      width: 14px;
      height: 14px;
    }
  }

  .not-checked-in {
    font-size: 12px;
    color: #9ca3af;
  }

  .ticket-code-text {
    padding: 10px 16px;
    background: #f9fafb;
    font-size: 10px;
    font-family: monospace;
    color: #9ca3af;
    text-align: center;
    border-top: 1px solid #e5e7eb;
    word-break: break-all;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.5rem;
  text-align: center;

  .empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.4;
  }

  .empty-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 0.5rem;
  }

  .empty-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }
}
</style>
